import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from './AppIcon';
import { sendRequirementsFormEmail } from '../utils/emailService';
import { useContactInfo } from '../utils/contactObfuscation';

const Chatbot = () => {
  // Load initial state from sessionStorage or use defaults
  const getInitialMessages = () => {
    try {
      const saved = sessionStorage.getItem('chatbot_messages');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Convert timestamp strings back to Date objects
        return parsed.map(msg => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      }
    } catch (error) {
      console.error('Error loading messages from sessionStorage:', error);
    }
    // Default initial message
    return [
      {
        id: 1,
        text: "Hello! 👋 I'm here to help you with your project needs. How would you like to proceed?\n\n1. Submit your project requirements\n2. Book a meeting\n3. Chat on WhatsApp\n4. Learn about our services\n\nWhat would you like to do? (Tell me the number or name)",
        sender: 'bot',
        timestamp: new Date()
      }
    ];
  };

  const getInitialConversationContext = () => {
    try {
      const saved = sessionStorage.getItem('chatbot_conversationContext');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading conversation context from sessionStorage:', error);
    }
    return {
      lastTopic: null,
      lastService: null,
      awaitingResponse: false,
      lastQuestion: null,
      lastOptions: null,
      conversationHistory: [],
      lastBotMessage: null,
      discussedTopics: []
    };
  };

  const getInitialRequirementsForm = () => {
    try {
      const saved = sessionStorage.getItem('chatbot_requirementsForm');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading requirements form from sessionStorage:', error);
    }
    return {
      name: '',
      email: '',
      phone: '',
      projectType: '',
      requirements: ''
    };
  };

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(getInitialMessages);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Project requirements form state
  const [isCollectingRequirements, setIsCollectingRequirements] = useState(() => {
    try {
      const saved = sessionStorage.getItem('chatbot_isCollectingRequirements');
      return saved === 'true';
    } catch (error) {
      return false;
    }
  });
  const [requirementsForm, setRequirementsForm] = useState(getInitialRequirementsForm);
  const [requirementsStep, setRequirementsStep] = useState(() => {
    try {
      const saved = sessionStorage.getItem('chatbot_requirementsStep');
      return saved ? parseInt(saved, 10) : 0;
    } catch (error) {
      return 0;
    }
  });
  const [isSubmittingRequirements, setIsSubmittingRequirements] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(() => {
    try {
      const saved = sessionStorage.getItem('chatbot_showQuickActions');
      return saved !== null ? saved === 'true' : true;
    } catch (error) {
      return true;
    }
  });
  
  // Conversation context for better flow handling
  const [conversationContext, setConversationContext] = useState(getInitialConversationContext);

  // Save messages to sessionStorage whenever they change
  useEffect(() => {
    try {
      sessionStorage.setItem('chatbot_messages', JSON.stringify(messages));
    } catch (error) {
      console.error('Error saving messages to sessionStorage:', error);
    }
  }, [messages]);

  // Save conversation context to sessionStorage whenever it changes
  useEffect(() => {
    try {
      sessionStorage.setItem('chatbot_conversationContext', JSON.stringify(conversationContext));
    } catch (error) {
      console.error('Error saving conversation context to sessionStorage:', error);
    }
  }, [conversationContext]);

  // Save requirements form state to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem('chatbot_requirementsForm', JSON.stringify(requirementsForm));
      sessionStorage.setItem('chatbot_isCollectingRequirements', String(isCollectingRequirements));
      sessionStorage.setItem('chatbot_requirementsStep', String(requirementsStep));
      sessionStorage.setItem('chatbot_showQuickActions', String(showQuickActions));
    } catch (error) {
      console.error('Error saving requirements form to sessionStorage:', error);
    }
  }, [requirementsForm, isCollectingRequirements, requirementsStep, showQuickActions]);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Contact information with links (using obfuscated phone and email)
  const obfuscatedContact = useContactInfo();
  const contactInfo = {
    email: obfuscatedContact.email,
    phone: obfuscatedContact.phone,
    phoneLink: obfuscatedContact.phoneLink,
    whatsapp: obfuscatedContact.whatsapp,
    location: 'Octalchip, 404, Opera point, kothariya, gujarat, india, 360022',
    locationLink: 'https://maps.google.com/?q=404+Opera+point+kothariya+gujarat+india+360022',
    emailLink: obfuscatedContact.mailtoLink,
    bookMeeting: 'https://cal.com/octalchip/30min'
  };

  // Helper function to get service details
  const getServiceDetails = (service) => {
    const serviceDetails = {
      'web': {
        text: `Web Development Services:\n\nWe build custom web applications using modern technologies:\n• Frontend: React, Next.js, Vue.js, Nuxt.js, TypeScript\n• Backend: Node.js, Python, Java, Go, PHP, Ruby\n• Features: Responsive design, SEO optimization, Progressive Web Apps (PWA)\n• Scalability: Microservices architecture, cloud deployment\n• Performance: Fast loading, optimized code, CDN integration\n\nOur web solutions are:\n✓ Fully responsive and mobile-friendly\n✓ SEO-optimized for better search rankings\n✓ Scalable to handle growing traffic\n✓ Secure with best practices\n\nPricing: Starting at $18/hour or $2560/month for dedicated developers.\n\nHow would you like to proceed?\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp`,
        hasLinks: false
      },
      'mobile': {
        text: `Mobile App Development Services:\n\nWe develop native and cross-platform mobile applications:\n• Platforms: iOS, Android, Cross-platform\n• Technologies: React Native, Flutter, Native iOS (Swift), Native Android (Kotlin)\n• Features: Push notifications, in-app purchases, offline capabilities\n• App Store: Complete submission and optimization\n\nOur mobile apps include:\n✓ Native performance and user experience\n✓ Cross-platform compatibility (iOS & Android)\n✓ App store optimization and deployment\n✓ Regular updates and maintenance\n\nTimeline: 6-12 weeks for typical mobile apps\nPricing: Starting at $18/hour or custom project-based pricing\n\nHow would you like to proceed?\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp`,
        hasLinks: false
      },
      'ai': {
        text: `AI/ML Services:\n\nWe provide comprehensive AI and Machine Learning solutions:\n• Machine Learning: Custom ML models, model training, deployment\n• Natural Language Processing (NLP): Text analysis, sentiment analysis, language models\n• Computer Vision: Image recognition, object detection, video analysis\n• Predictive Analytics: Forecasting, trend analysis, data-driven insights\n• AI Chatbots: Intelligent conversational agents, customer support automation\n• Deep Learning: Neural networks, deep learning models\n• Data Science: Data analysis, visualization, business intelligence\n\nOur AI solutions:\n✓ Custom AI models tailored to your business\n✓ Integration with existing systems\n✓ Real-time processing and analytics\n✓ Continuous learning and improvement\n\nPricing: Starting at $25/hour for AI development\n\nHow would you like to proceed?\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp`,
        hasLinks: false
      },
      'cloud': {
        text: `Cloud & DevOps Services:\n\nWe provide cloud infrastructure and DevOps solutions:\n• Cloud Platforms: AWS, Azure, Google Cloud Platform (GCP)\n• Containerization: Docker, Kubernetes orchestration\n• CI/CD: Automated pipelines, continuous integration/deployment\n• Infrastructure: Server setup, scaling, load balancing\n• Monitoring: Performance monitoring, logging, alerting\n• Security: Cloud security, compliance, backup solutions\n\nOur cloud services:\n✓ Scalable and reliable infrastructure\n✓ Automated deployment pipelines\n✓ 24/7 monitoring and support\n✓ Cost optimization and resource management\n\nPricing: Starting at $20/hour for cloud services\n\nHow would you like to proceed?\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp`,
        hasLinks: false
      },
      'design': {
        text: `UI/UX Design Services:\n\nWe create user-centered designs that enhance user experience:\n• User Research: User interviews, surveys, persona development\n• Wireframing: Low and high-fidelity wireframes\n• Prototyping: Interactive prototypes, user testing\n• Design Systems: Consistent design language, component libraries\n• Usability Testing: User feedback, iterative improvements\n• Responsive Design: Mobile-first, tablet, desktop layouts\n\nOur design process:\n✓ User-centered approach\n✓ Modern and intuitive interfaces\n✓ Accessibility compliance\n✓ Brand consistency\n\nHow would you like to proceed?\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp`,
        hasLinks: false
      },
      'backend': {
        text: `Backend Development Services:\n\nWe build robust and scalable backend systems:\n• Architecture: Microservices, monolithic, serverless\n• APIs: RESTful APIs, GraphQL, WebSocket\n• Databases: SQL (PostgreSQL, MySQL), NoSQL (MongoDB, Redis)\n• Technologies: Node.js, Python, Java, Go, PHP, Ruby\n• Features: Authentication, authorization, real-time communication\n• Performance: Optimization, caching, load balancing\n\nOur backend solutions:\n✓ Scalable and maintainable architecture\n✓ Secure APIs with authentication\n✓ Database optimization\n✓ Real-time capabilities\n\nHow would you like to proceed?\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp`,
        hasLinks: false
      },
      'automation': {
        text: `Workflow Automation & Integrations Services:\n\nWe design, build, and optimize automated workflows using:\n• n8n Automation: Self-hosted workflow automation for full control and security\n• Zapier Integrations: Fast and reliable integrations between popular SaaS tools\n• Make Automation: Advanced visual workflows with complex logic and data transformation\n• Features: API integrations, webhooks, scheduled jobs, error handling\n• Use Cases: CRM automation, marketing workflows, data pipelines, e-commerce integration\n\nOur automation solutions:\n✓ Connect your tools and eliminate manual work\n✓ Improve operational efficiency\n✓ Secure and scalable architectures\n✓ Long-term support and optimization\n\nTimeline: 1-4 weeks for typical automation projects\nPricing: Starting at $18/hour\n\nHow would you like to proceed?\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp`,
        hasLinks: false
      }
    };
    return serviceDetails[service] || null;
  };

  // Helper to update conversation history
  const updateConversationHistory = (userMessage, botResponse) => {
    const botResponseText = typeof botResponse === 'string' ? botResponse : botResponse.text;
    setConversationContext(prev => {
      // Ensure conversationHistory exists and is an array
      const currentHistory = Array.isArray(prev?.conversationHistory) ? prev.conversationHistory : [];
      const newHistory = [
        ...currentHistory.slice(-4), // Keep last 5 entries (2.5 exchanges)
        { user: userMessage, bot: botResponseText }
      ];
      
      // Track discussed topics - ensure it's an array
      const currentTopics = Array.isArray(prev?.discussedTopics) ? prev.discussedTopics : [];
      const topics = [...currentTopics];
      const lowerUserMsg = userMessage.toLowerCase();
      if (lowerUserMsg.includes('service') && !topics.includes('services')) topics.push('services');
      if (lowerUserMsg.includes('price') && !topics.includes('pricing')) topics.push('pricing');
      if (lowerUserMsg.includes('web') && !topics.includes('web')) topics.push('web');
      if (lowerUserMsg.includes('mobile') && !topics.includes('mobile')) topics.push('mobile');
      if (lowerUserMsg.includes('ai') || lowerUserMsg.includes('ml')) {
        if (!topics.includes('ai')) topics.push('ai');
      }
      
      return {
        ...prev,
        conversationHistory: newHistory,
        lastBotMessage: botResponseText,
        discussedTopics: topics.slice(-5) // Keep last 5 topics
      };
    });
  };

  // Helper function to map service variations to actual service keys
  const mapServiceVariation = (message) => {
    const lowerMsg = message.toLowerCase();
    
    // Mobile App variations
    if (lowerMsg.match(/\b(app|apps|application|mobile|mobile app|ios|android|react native|flutter|native app|phone app|smartphone app)\b/)) {
      return 'mobile';
    }
    
    // Web Development variations
    if (lowerMsg.match(/\b(web|website|web app|web development|web site|frontend|react|next\.js|vue|html|css)\b/)) {
      return 'web';
    }
    
    // AI/ML variations
    if (lowerMsg.match(/\b(ai|artificial intelligence|machine learning|ml|chatbot|nlp|computer vision|deep learning|data science|predictive analytics|neural network)\b/)) {
      return 'ai';
    }
    
    // Cloud & DevOps variations
    if (lowerMsg.match(/\b(cloud|devops|aws|azure|gcp|docker|kubernetes|ci\/cd|infrastructure|deployment|server)\b/)) {
      return 'cloud';
    }
    
    // UI/UX Design variations
    if (lowerMsg.match(/\b(ui|ux|design|user interface|user experience|wireframe|prototype|usability|ui design|ux design)\b/)) {
      return 'design';
    }
    
    // Backend variations
    if (lowerMsg.match(/\b(backend|api|database|server|microservices|rest api|graphql|backend development)\b/)) {
      return 'backend';
    }
    
    // Workflow Automation variations
    if (lowerMsg.match(/\b(workflow|automation|zapier|n8n|make|integromat|integration|process automation|business automation|workflow automation)\b/)) {
      return 'automation';
    }
    
    return null;
  };

  // Helper function to extract numbered options from bot message
  const extractNumberedOptions = (botMessage) => {
    const options = [];
    const lines = botMessage.split('\n');
    
    for (const line of lines) {
      // Match patterns like "1. Option text" or "• Option text" or "- Option text"
      const numberedMatch = line.match(/^(\d+)\.\s*(.+)$/);
      const bulletMatch = line.match(/^[•\-\*]\s*(.+)$/);
      
      if (numberedMatch) {
        const num = parseInt(numberedMatch[1]);
        const text = numberedMatch[2].trim();
        options.push({ number: num, text: text });
      } else if (bulletMatch && options.length > 0) {
        // If we have numbered options, treat bullets as continuation
        // Otherwise, treat as option 1, 2, 3...
        const text = bulletMatch[1].trim();
        if (options.length === 0 || options[options.length - 1].number) {
          options.push({ number: options.length + 1, text: text });
        }
      }
    }
    
    return options;
  };

  // Helper to detect pronouns and resolve them
  const resolvePronouns = (message, context) => {
    const lowerMessage = message.toLowerCase();
    
    // Handle "it", "that", "this" referring to last topic/service
    if (lowerMessage.match(/\b(it|that|this|them|those)\b/)) {
      if (context?.lastService) {
        return `about ${context.lastService} development`;
      }
      if (context?.lastTopic) {
        return `about ${context.lastTopic}`;
      }
      if (context?.lastBotMessage) {
        // Extract key topic from last bot message
        const lastMsg = context.lastBotMessage.toLowerCase();
        if (lastMsg.includes('web development')) return 'about web development';
        if (lastMsg.includes('mobile app')) return 'about mobile app development';
        if (lastMsg.includes('ai') || lastMsg.includes('ml')) return 'about AI/ML services';
        if (lastMsg.includes('pricing')) return 'about pricing';
        if (lastMsg.includes('service')) return 'about services';
      }
    }
    return message;
  };

  // Predefined responses based on keywords
  const getBotResponse = (userMessage, context = null, lastBotMessageText = null) => {
    const lowerMessage = userMessage.toLowerCase();
    const resolvedMessage = resolvePronouns(userMessage, context);
    const lowerResolved = resolvedMessage.toLowerCase();
    
    // Get the last bot message - check context first, then passed parameter
    let lastBotMsg = context?.lastBotMessage || lastBotMessageText;
    
    // Handle numeric or text selection from main action lists (when no context.lastOptions)
    // First check for A, B, C options (3-option format: Submit, Schedule, WhatsApp)
    if (!context?.lastOptions && lastBotMsg) {
      // Check if last message has A, B, C action options (3-option format)
      if (lastBotMsg.includes('A. Submit') && lastBotMsg.includes('B. Schedule') && lastBotMsg.includes('C. Chat')) {
        const trimmedMessage = userMessage.trim();
        const upperMessage = trimmedMessage.toUpperCase();
        let selectedAction = null;
        
        // Check for letter selection (A, B, C)
        if (/^[A-C]$/i.test(trimmedMessage)) {
          selectedAction = upperMessage;
        }
        // Check for text-based selection
        else {
          const lowerInput = lowerMessage;
          if (lowerInput.match(/\b(submit|requirements|project requirements)\b/)) {
            selectedAction = 'A';
          } else if (lowerInput.match(/\b(schedule|consultation|book|meeting|call|appointment)\b/)) {
            selectedAction = 'B';
          } else if (lowerInput.match(/\b(whatsapp|chat|message)\b/)) {
            selectedAction = 'C';
          }
        }
        
        if (selectedAction) {
          if (selectedAction === 'A') {
            // Submit requirements
            setIsCollectingRequirements(true);
            setRequirementsStep(0);
            setRequirementsForm({ name: '', email: '', phone: '', projectType: '', requirements: '' });
            setConversationContext(prev => ({ ...prev, awaitingResponse: false }));
            return {
              text: `Perfect! Let me collect your project requirements.\n\nStep 1 of 5: What's your name?\n\n(You can type "cancel" at any time to stop)`,
              hasLinks: false
            };
          } else if (selectedAction === 'B') {
            // Schedule consultation
            window.open(contactInfo.bookMeeting, '_blank', 'noopener,noreferrer');
            setConversationContext(prev => ({ ...prev, awaitingResponse: false }));
            return {
              text: `Perfect! I've opened the booking page for you. You can schedule a free 30-minute consultation.\n\nBook a Meeting: Click to schedule`,
              hasLinks: true
            };
          } else if (selectedAction === 'C') {
            // WhatsApp
            if (contactInfo.whatsapp) {
              window.open(contactInfo.whatsapp, '_blank', 'noopener,noreferrer');
            }
            setConversationContext(prev => ({ ...prev, awaitingResponse: false }));
            return {
              text: `Great! I've opened WhatsApp for you. You can chat directly with our team.\n\nWhatsApp: Click to chat`,
              hasLinks: true
            };
          }
        }
      }
      // Check if last message has numbered action options (1-4) for main actions
      else if (lastBotMsg.includes('1. Submit') && lastBotMsg.includes('2. Book') && lastBotMsg.includes('3. Chat') && lastBotMsg.includes('4. Learn')) {
        const trimmedMessage = userMessage.trim();
        let selectedAction = null;
        
        // Check for numeric selection (1-4)
        if (/^\d+$/.test(trimmedMessage)) {
          const selectedNumber = parseInt(trimmedMessage);
          if (selectedNumber >= 1 && selectedNumber <= 4) {
            selectedAction = selectedNumber;
          }
        }
        // Check for text-based selection
        else {
          const lowerInput = lowerMessage;
          if (lowerInput.match(/\b(submit|requirements|project requirements|requirements)\b/)) {
            selectedAction = 1;
          } else if (lowerInput.match(/\b(book|meeting|schedule|consultation|call|appointment)\b/)) {
            selectedAction = 2;
          } else if (lowerInput.match(/\b(whatsapp|chat|message)\b/)) {
            selectedAction = 3;
          } else if (lowerInput.match(/\b(learn|services|service|what.*offer|what.*do)\b/)) {
            selectedAction = 4;
          }
        }
        
        if (selectedAction) {
          if (selectedAction === 1) {
            // Submit requirements
            setIsCollectingRequirements(true);
            setRequirementsStep(0);
            setRequirementsForm({ name: '', email: '', phone: '', projectType: '', requirements: '' });
            setConversationContext(prev => ({ ...prev, awaitingResponse: false }));
            return {
              text: `Perfect! Let me collect your project requirements.\n\nStep 1 of 5: What's your name?\n\n(You can type "cancel" at any time to stop)`,
              hasLinks: false
            };
          } else if (selectedAction === 2) {
            // Book meeting
            window.open(contactInfo.bookMeeting, '_blank', 'noopener,noreferrer');
            setConversationContext(prev => ({ ...prev, awaitingResponse: false }));
            return {
              text: `Perfect! I've opened the booking page for you. You can schedule a free 30-minute consultation.\n\nBook a Meeting: Click to schedule`,
              hasLinks: true
            };
          } else if (selectedAction === 3) {
            // WhatsApp
            if (contactInfo.whatsapp) {
              window.open(contactInfo.whatsapp, '_blank', 'noopener,noreferrer');
            }
            setConversationContext(prev => ({ ...prev, awaitingResponse: false }));
            return {
              text: `Great! I've opened WhatsApp for you. You can chat directly with our team.\n\nWhatsApp: Click to chat`,
              hasLinks: true
            };
          } else if (selectedAction === 4) {
            // Learn about services
            setConversationContext(prev => ({ 
              ...prev,
              lastTopic: 'services', 
              lastService: null, 
              awaitingResponse: true, 
              lastQuestion: 'service_details',
              lastBotMessage: `We offer:\n\n1. Web Development - React, Next.js, Vue.js\n2. Mobile App Development - iOS, Android, React Native, Flutter\n3. AI/ML Services - Custom ML models, NLP, chatbots\n4. Cloud & DevOps - AWS, Azure, GCP, Docker, Kubernetes\n5. UI/UX Design - User research, wireframing, prototyping\n6. Backend Development - Microservices, APIs, databases\n7. Workflow Automation - n8n, Zapier, Make integrations\n\nWhich service interests you? (Tell me the number or name)\n\nOr would you like to:\nA. Submit project requirements\nB. Book a meeting\nC. Chat on WhatsApp\n\n(Tell me the service number 1-7, or action letter A-C)`
            }));
            return {
              text: `We offer:\n\n1. Web Development - React, Next.js, Vue.js\n2. Mobile App Development - iOS, Android, React Native, Flutter\n3. AI/ML Services - Custom ML models, NLP, chatbots\n4. Cloud & DevOps - AWS, Azure, GCP, Docker, Kubernetes\n5. UI/UX Design - User research, wireframing, prototyping\n6. Backend Development - Microservices, APIs, databases\n7. Workflow Automation - n8n, Zapier, Make integrations\n\nWhich service interests you? (Tell me the number or name)\n\nOr would you like to:\nA. Submit project requirements\nB. Book a meeting\nC. Chat on WhatsApp\n\n(Tell me the service number 1-7, or action letter A-C)`,
              hasLinks: false
            };
          }
        }
      }
    }
    
    // Handle numeric quick replies (1, 2, 3, etc.) or "option 1", "option 2", etc.
    if (context?.lastOptions) {
      let selectedOption = null;
      const trimmedMessage = userMessage.trim();
      
      // Check for pure number (1, 2, 3)
      if (/^\d+$/.test(trimmedMessage)) {
        selectedOption = parseInt(trimmedMessage);
      }
      // Check for "option 1", "option 2", etc.
      else if (lowerMessage.match(/^option\s*(\d+)$/)) {
        const match = lowerMessage.match(/^option\s*(\d+)$/);
        selectedOption = parseInt(match[1]);
      }
      // Check for text matching option content
      else {
        const options = context.lastOptions;
        for (let i = 0; i < options.length; i++) {
          const optionText = options[i].text.toLowerCase();
          // Check if user message contains key words from the option
          const keyWords = optionText.split(' ').filter(word => word.length > 3);
          const matches = keyWords.filter(word => lowerMessage.includes(word));
          if (matches.length >= 2 || (keyWords.length <= 3 && matches.length >= 1)) {
            selectedOption = i + 1;
            break;
          }
        }
      }
      
      if (selectedOption && selectedOption >= 1 && selectedOption <= context.lastOptions.length) {
        const selectedAction = context.lastOptions[selectedOption - 1];
        setConversationContext(prev => ({ 
          ...prev, 
          awaitingResponse: false, 
          lastOptions: null 
        }));
        
        // Execute the selected action
        if (selectedAction.action === 'submit_requirements') {
          setIsCollectingRequirements(true);
          setRequirementsStep(0);
          setRequirementsForm({ name: '', email: '', phone: '', projectType: '', requirements: '' });
          return {
            text: `Perfect! Let me collect your project requirements.\n\nStep 1 of 5: What's your name?\n\n(You can type "cancel" at any time to stop)`,
            hasLinks: false
          };
        } else if (selectedAction.action === 'schedule_consultation' || selectedAction.action === 'book_consultation') {
          window.open(contactInfo.bookMeeting, '_blank', 'noopener,noreferrer');
          return {
            text: `Perfect! I've opened the booking page for you. You can schedule a free 30-minute consultation.\n\nBook a Meeting: Click to schedule`,
            hasLinks: true
          };
        } else if (selectedAction.action === 'whatsapp') {
          if (contactInfo.whatsapp) {
            window.open(contactInfo.whatsapp, '_blank', 'noopener,noreferrer');
          }
          return {
            text: `Great! I've opened WhatsApp for you. You can chat directly with our team.\n\nWhatsApp: Click to chat`,
            hasLinks: true
          };
        } else if (selectedAction.action === 'contact_directly') {
          return {
            text: `You can reach us directly through:\n\nEmail: ${contactInfo.email}\nPhone: ${contactInfo.phone}\nWhatsApp: Click to chat\nBook a Meeting: Click to schedule\n\nOur office hours are Mon-Fri, 9AM-6PM.`,
            hasLinks: true
          };
        }
      } else if (selectedOption && (selectedOption < 1 || selectedOption > context.lastOptions.length)) {
        // Invalid option number
        return {
          text: `I didn't recognize that option. Please choose a number between 1 and ${context.lastOptions.length}, or type the option name.\n\n${context.lastOptions.map((opt, idx) => `${idx + 1}. ${opt.text}`).join('\n')}`,
          hasLinks: false
        };
      }
    }
    
    // Handle numerical or letter selection from services list (when bot just listed services)
    // Check if last bot message contains services list (numbered or bulleted)
    const lastBotMsgForServices = lastBotMsg || context?.lastBotMessage;
    if (lastBotMsgForServices && (lastBotMsgForServices.includes('•') || lastBotMsgForServices.includes('Web Development') || lastBotMsgForServices.match(/\d+\.\s*(Web|Mobile|AI|Cloud|UI|Backend|Workflow)/))) {
      const trimmedMessage = userMessage.trim();
      const upperMessage = trimmedMessage.toUpperCase();
      
      // Check if user typed a letter (A, B, C) or text for action options
      if (lastBotMsgForServices.includes('A. Submit') && (lastBotMsgForServices.includes('B. Book') || lastBotMsgForServices.includes('B. Schedule')) && lastBotMsgForServices.includes('C. Chat')) {
        let selectedAction = null;
        
        // Check for letter selection (A, B, C)
        if (/^[A-C]$/i.test(trimmedMessage)) {
          selectedAction = upperMessage;
        }
        // Check for text-based selection
        else {
          const lowerInput = lowerMessage;
          if (lowerInput.match(/\b(submit|requirements|project requirements)\b/)) {
            selectedAction = 'A';
          } else if (lowerInput.match(/\b(book|meeting|schedule|consultation|call|appointment)\b/)) {
            selectedAction = 'B';
          } else if (lowerInput.match(/\b(whatsapp|chat|message)\b/)) {
            selectedAction = 'C';
          }
        }
        
        if (selectedAction) {
          if (selectedAction === 'A') {
            // Submit requirements
            setIsCollectingRequirements(true);
            setRequirementsStep(0);
            setRequirementsForm({ name: '', email: '', phone: '', projectType: '', requirements: '' });
            setConversationContext(prev => ({ ...prev, awaitingResponse: false }));
            return {
              text: `Perfect! Let me collect your project requirements.\n\nStep 1 of 5: What's your name?\n\n(You can type "cancel" at any time to stop)`,
              hasLinks: false
            };
          } else if (selectedAction === 'B') {
            // Book meeting
            window.open(contactInfo.bookMeeting, '_blank', 'noopener,noreferrer');
            setConversationContext(prev => ({ ...prev, awaitingResponse: false }));
            return {
              text: `Perfect! I've opened the booking page for you. You can schedule a free 30-minute consultation.\n\nBook a Meeting: Click to schedule`,
              hasLinks: true
            };
          } else if (selectedAction === 'C') {
            // WhatsApp
            if (contactInfo.whatsapp) {
              window.open(contactInfo.whatsapp, '_blank', 'noopener,noreferrer');
            }
            setConversationContext(prev => ({ ...prev, awaitingResponse: false }));
            return {
              text: `Great! I've opened WhatsApp for you. You can chat directly with our team.\n\nWhatsApp: Click to chat`,
              hasLinks: true
            };
          }
        }
      }
      
      // Check if user typed just a number (for services 1-7)
      if (/^\d+$/.test(trimmedMessage)) {
        const selectedNumber = parseInt(trimmedMessage);
        const services = [
          { number: 1, name: 'Web Development', key: 'web' },
          { number: 2, name: 'Mobile App Development', key: 'mobile' },
          { number: 3, name: 'AI/ML Services', key: 'ai' },
          { number: 4, name: 'Cloud & DevOps', key: 'cloud' },
          { number: 5, name: 'UI/UX Design', key: 'design' },
          { number: 6, name: 'Backend Development', key: 'backend' },
          { number: 7, name: 'Workflow Automation & Integrations', key: 'automation' }
        ];
        
        const selectedService = services.find(s => s.number === selectedNumber);
        if (selectedService && selectedNumber >= 1 && selectedNumber <= 7) {
          const details = getServiceDetails(selectedService.key);
          if (details) {
            setConversationContext(prev => ({ 
              ...prev, 
              lastService: selectedService.key,
              lastTopic: 'services',
              awaitingResponse: true,
              lastQuestion: 'discuss_project',
              lastBotMessage: details.text
            }));
            return details;
          }
        } else if (selectedNumber < 1 || selectedNumber > 7) {
          // Invalid service number
          return {
            text: `I didn't recognize that service number. Please choose a number between 1 and 7, or type the service name.\n\n1. Web Development\n2. Mobile App Development\n3. AI/ML Services\n4. Cloud & DevOps\n5. UI/UX Design\n6. Backend Development\n7. Workflow Automation & Integrations`,
            hasLinks: false
          };
        }
      }
    }
    
    // Handle short/descriptive service names (e.g., "app services", "mobile", "web dev")
    // Check if this might be a service selection after services were listed
    if (context?.lastBotMessage && (context.lastBotMessage.includes('•') || context.lastBotMessage.includes('Web Development') || context.lastBotMessage.includes('Mobile App'))) {
      const serviceKey = mapServiceVariation(userMessage);
      if (serviceKey) {
        const details = getServiceDetails(serviceKey);
        if (details) {
          setConversationContext(prev => ({ 
            ...prev, 
            lastService: serviceKey,
            lastTopic: 'services',
            awaitingResponse: true,
            lastQuestion: 'discuss_project'
          }));
          return details;
        }
      }
    }
    
    // Handle standalone service mentions (not in "I need" context)
    // This catches cases like "app services", "mobile", "web dev" when user types them directly
    const serviceKey = mapServiceVariation(userMessage);
    if (serviceKey && !lowerMessage.match(/^(i need|i want|i'm looking for|i require|i'd like)/)) {
      // Only trigger if message is short/descriptive (likely a service selection)
      // or if context suggests services were just discussed
      if (userMessage.trim().length < 50 || (context?.lastBotMessage && context.lastBotMessage.includes('service'))) {
        const details = getServiceDetails(serviceKey);
        if (details) {
          setConversationContext(prev => ({ 
            ...prev, 
            lastService: serviceKey,
            lastTopic: 'services',
            awaitingResponse: true,
            lastQuestion: 'discuss_project'
          }));
          return details;
        }
      }
    }
    
    // Examples/Case Studies - handle requests to see examples (check this early)
    // This should be checked before other handlers to catch "examples" requests
    if (lowerMessage.match(/\b(examples|case studies|see examples|show examples|view examples|show me examples|i want to see|can i see|let me see)\b/)) {
      setConversationContext(prev => ({ 
        ...prev, 
        lastTopic: 'portfolio',
        awaitingResponse: false,
        lastQuestion: null
      }));
      // Open blog page in new tab
      window.open('/blog', '_blank', 'noopener,noreferrer');
      return {
        text: `Great! I've opened our blog section where you can see detailed case studies of our work.\n\nOur case studies include:\n• FinTech solutions - Payment platforms, banking apps, investment platforms\n• E-commerce platforms - Online stores, marketplace solutions\n• Healthcare applications - Medical apps, patient management systems\n• Logistics systems - Fleet management, tracking platforms\n• Manufacturing solutions - IoT systems, automation platforms\n• Real Estate platforms - Property management systems\n\nEach case study provides:\n✓ Project overview and challenges\n✓ Solutions we implemented\n✓ Technologies used\n✓ Results and business impact\n\nIf the blog page didn't open, you can visit our blog at /blog\n\nWould you like to know more about any specific industry or project type?`,
        hasLinks: false
      };
    }
    
    // Handle "NO" responses to any question - check this BEFORE yes/no in context
    // Check if last bot message contains a question
    if (context?.lastBotMessage) {
      const lastBotMsg = context.lastBotMessage.toLowerCase();
      const hasQuestion = lastBotMsg.includes('would you like') || 
                         lastBotMsg.includes('do you want') || 
                         lastBotMsg.includes('can you') ||
                         lastBotMsg.includes('will you') ||
                         lastBotMsg.includes('are you') ||
                         lastBotMsg.includes('would you') ||
                         lastBotMsg.includes('do you') ||
                         lastBotMsg.includes('which') ||
                         lastBotMsg.includes('what would') ||
                         lastBotMsg.match(/\?/); // Contains question mark
      
      if (hasQuestion && lowerMessage.match(/\b(no|nope|nah|not really|not interested|maybe later|not now|not at the moment|not right now|maybe some other time|not yet|pass|skip)\b/)) {
        setConversationContext(prev => ({ 
          ...prev, 
          awaitingResponse: false, 
          lastOptions: null,
          lastQuestion: null
        }));
        setShowQuickActions(true); // Show quick actions when user says no
        
        // Provide context-aware response based on what was asked
        if (lastBotMsg.includes('discuss') || lastBotMsg.includes('project')) {
          return {
            text: `No problem! That's completely fine. Is there anything else I can help you with today? Feel free to ask about:\n\n• Our services\n• Pricing information\n• Technologies we use\n• Our portfolio\n• Contact information\n\nOr just let me know what you'd like to know!`,
            hasLinks: false
          };
        }
        if (lastBotMsg.includes('service') || lastBotMsg.includes('know more')) {
          return {
            text: `That's okay! Is there something else you'd like to know about? I can help with:\n\n• Pricing\n• Technologies\n• Portfolio\n• Contact information\n• Or any other questions\n\nWhat would you like to explore?`,
            hasLinks: false
          };
        }
        if (lastBotMsg.includes('consultation') || lastBotMsg.includes('meeting') || lastBotMsg.includes('schedule')) {
          return {
            text: `That's fine! You can always schedule a consultation later if needed. Is there anything else I can help you with right now?`,
            hasLinks: false
          };
        }
        if (lastBotMsg.includes('portfolio') || lastBotMsg.includes('examples') || lastBotMsg.includes('case studies')) {
          return {
            text: `No problem! Is there something else you'd like to know about? I can help with:\n\n• Our services\n• Pricing information\n• Technologies we use\n• Contact information\n• Or any other questions\n\nWhat would you like to explore?`,
            hasLinks: false
          };
        }
        
        // Generic "no" response
        return {
          text: `No problem! Is there anything else I can help you with today? Feel free to ask about our services, pricing, or any other questions.`,
          hasLinks: false
        };
      }
    }
    
    // Handle Yes/No responses based on context
    if (context?.awaitingResponse) {
      if (lowerMessage.match(/\b(yes|yeah|yep|yup|sure|ok|okay|absolutely|definitely|of course|correct|right)\b/)) {
        if (context.lastQuestion === 'service_details') {
          setConversationContext(prev => ({ ...prev, awaitingResponse: false }));
          return {
            text: `Great! Which specific service would you like to know more about?\n\n• Web Development\n• Mobile App Development\n• AI/ML Services\n• Cloud & DevOps\n• UI/UX Design\n• Backend Development\n\nJust let me know which one interests you!`,
            hasLinks: false
          };
        }
        // Handle "discuss_project" - user wants to discuss their project
        if (context.lastQuestion === 'discuss_project') {
          const options = [
            { action: 'submit_requirements', text: 'Submit your project requirements' },
            { action: 'schedule_consultation', text: 'Schedule a consultation' },
            { action: 'whatsapp', text: 'Chat on WhatsApp' }
          ];
          setConversationContext(prev => ({ 
            ...prev, 
            awaitingResponse: true, 
            lastOptions: options,
            lastQuestion: 'project_options'
          }));
          return {
            text: `Great! How would you like to proceed?\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp`,
            hasLinks: false
          };
        }
        // Handle "portfolio_examples" - user wants to see examples
        if (context.lastQuestion === 'portfolio_examples') {
          // Open blog page in new tab
          window.open('/blog', '_blank', 'noopener,noreferrer');
          setConversationContext(prev => ({ 
            ...prev, 
            awaitingResponse: false,
            lastQuestion: null
          }));
          return {
            text: `Perfect! I've opened our blog section where you can see detailed case studies of our work.\n\nOur case studies include:\n• FinTech solutions - Payment platforms, banking apps, investment platforms\n• E-commerce platforms - Online stores, marketplace solutions\n• Healthcare applications - Medical apps, patient management systems\n• Logistics systems - Fleet management, tracking platforms\n• Manufacturing solutions - IoT systems, automation platforms\n• Real Estate platforms - Property management systems\n\nEach case study provides:\n✓ Project overview and challenges\n✓ Solutions we implemented\n✓ Technologies used\n✓ Results and business impact\n\nIf the blog page didn't open, you can visit our blog at /blog\n\nWould you like to know more about any specific industry or project type?`,
            hasLinks: false
          };
        }
        if (context.lastQuestion === 'submit_requirements') {
          setIsCollectingRequirements(true);
          setRequirementsStep(0);
          setRequirementsForm({ name: '', email: '', phone: '', projectType: '', requirements: '' });
          setConversationContext(prev => ({ ...prev, awaitingResponse: false }));
          return {
            text: `Perfect! Let me collect your project requirements.\n\nStep 1 of 5: What's your name?\n\n(You can type "cancel" at any time to stop)`,
            hasLinks: false
          };
        }
        // If last question is not set but we have a lastService, treat as discuss_project
        if (!context.lastQuestion && context.lastService) {
          const options = [
            { action: 'submit_requirements', text: 'Submit your project requirements' },
            { action: 'schedule_consultation', text: 'Schedule a consultation' },
            { action: 'whatsapp', text: 'Chat on WhatsApp' }
          ];
          setConversationContext(prev => ({ 
            ...prev, 
            awaitingResponse: true, 
            lastOptions: options,
            lastQuestion: 'project_options'
          }));
          const serviceName = context.lastService === 'web' ? 'web development' : 
                             context.lastService === 'mobile' ? 'mobile app' : 
                             context.lastService === 'ai' ? 'AI/ML' : 
                             context.lastService === 'cloud' ? 'cloud' :
                             context.lastService === 'design' ? 'design' :
                             context.lastService === 'backend' ? 'backend' :
                             context.lastService === 'automation' ? 'automation' : 'project';
          return {
            text: `Perfect! How would you like to proceed with your ${serviceName} project?\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp`,
            hasLinks: false
          };
        }
      }
      // "No" responses are now handled above for broader question detection
      // This fallback is for cases where awaitingResponse is true but no question was detected
      if (lowerMessage.match(/\b(no|nope|nah|not really|not interested|maybe later|not now|not at the moment|not right now|maybe some other time|not yet|pass|skip)\b/)) {
        setConversationContext(prev => ({ ...prev, awaitingResponse: false, lastOptions: null }));
        setShowQuickActions(true);
        return {
          text: `No problem! Is there anything else I can help you with today? Feel free to ask about our services, pricing, or any other questions.`,
          hasLinks: false
        };
      }
    }
    
    // Handle pronouns and references to previous messages
    if (lowerMessage.match(/\b(it|that|this|them|those|the same|same thing)\b/)) {
      if (context?.lastService) {
        const details = getServiceDetails(context.lastService);
        if (details) {
          setConversationContext(prev => ({ 
            ...prev, 
            lastTopic: 'services',
            lastService: context.lastService
          }));
          return details;
        }
      }
      if (context?.lastTopic === 'pricing') {
        return {
          text: `Our Pricing Structure:\n\nWe offer flexible pricing models to suit different project needs:\n\n• Hourly Engagement: $18/hour\n  Best for: Small projects, MVPs, ongoing maintenance\n\n• Monthly Dedicated Developer: $2560/month\n  Best for: Ongoing development, long-term projects\n\n• Fixed Cost Projects: Custom quote\n  Best for: Complete solutions with defined scope\n\n• AI Development: $25/hour\n  Best for: AI integration, ML model development\n\n• Cloud Services: $20/hour\n  Best for: Infrastructure setup, cloud migration\n\nFor a detailed quote tailored to your project, please contact us:\n\nEmail: ${contactInfo.email}\nPhone: ${contactInfo.phone}\nWhatsApp: Click to chat\nBook a Meeting: Click to schedule`,
          hasLinks: true
        };
      }
      if (context?.lastBotMessage) {
        // Try to understand what "it" refers to from last message
        const lastMsg = context.lastBotMessage.toLowerCase();
        if (lastMsg.includes('web development') || lastMsg.includes('web app')) {
          setConversationContext({ 
            ...conversationContext, 
            lastService: 'web',
            lastTopic: 'services'
          });
          return getServiceDetails('web');
        }
        if (lastMsg.includes('mobile app') || lastMsg.includes('mobile development')) {
          setConversationContext({ 
            ...conversationContext, 
            lastService: 'mobile',
            lastTopic: 'services'
          });
          return getServiceDetails('mobile');
        }
        if (lastMsg.includes('ai') || lastMsg.includes('ml') || lastMsg.includes('machine learning')) {
        setConversationContext(prev => ({ 
          ...prev, 
          lastService: 'ai',
          lastTopic: 'services'
        }));
          return getServiceDetails('ai');
        }
      }
    }
    
    // Handle follow-up questions
    if (lowerMessage.match(/\b(tell me more|more details|more info|more information|elaborate|explain more|can you explain|what about|how about|what else|anything else|go on|continue)\b/)) {
      if (context?.lastService) {
        // Return more details about the last service discussed
        const details = getServiceDetails(context.lastService);
        if (details) {
          setConversationContext(prev => ({ 
            ...prev, 
            lastTopic: 'services',
            lastService: context.lastService
          }));
          return details;
        }
      }
      if (context?.lastTopic === 'pricing') {
        const options = [
          { action: 'book_consultation', text: 'Book a free consultation to discuss your specific project needs' },
          { action: 'submit_requirements', text: 'Submit your project requirements' },
          { action: 'contact_directly', text: 'Contact us directly for immediate assistance' }
        ];
        setConversationContext(prev => ({ 
          ...prev, 
          awaitingResponse: true, 
          lastOptions: options,
          lastQuestion: 'pricing_options'
        }));
        return {
          text: `For more detailed pricing information, I'd recommend:\n\n1. Book a free consultation to discuss your specific project needs\n2. Submit your project requirements\n3. Contact us directly for immediate assistance\n\nWould you like to proceed with any of these options?`,
          hasLinks: false
        };
      }
      if (context?.lastTopic) {
        // Provide more info based on last topic
        if (context.lastTopic === 'services') {
          return {
            text: `I'd be happy to provide more details about our services! Which specific service would you like to know more about?\n\n• Web Development\n• Mobile App Development\n• AI/ML Services\n• Cloud & DevOps\n• UI/UX Design\n• Backend Development\n• Workflow Automation & Integrations\n\nJust let me know!`,
            hasLinks: false
          };
        }
      }
      return {
        text: `I'd be happy to provide more details! Could you please specify what you'd like to know more about?\n\n• Services\n• Pricing\n• Technologies\n• Portfolio\n• Support\n\nOr feel free to ask any specific question!`,
        hasLinks: false
      };
    }
    
    // Handle "what" questions in context
    if (lowerMessage.match(/^what (is|are|do|does|can|will)/)) {
      // Check if asking about something from context
      if (context?.lastService) {
        const details = getServiceDetails(context.lastService);
        if (details) {
          setConversationContext(prev => ({ 
            ...prev, 
            lastTopic: 'services',
            lastService: context.lastService
          }));
          return details;
        }
      }
      // Check if asking about something mentioned in last bot message
      if (context?.lastBotMessage) {
        const lastMsg = context.lastBotMessage.toLowerCase();
        if (lastMsg.includes('web development') && lowerResolved.includes('web')) {
          setConversationContext({ 
            ...conversationContext, 
            lastService: 'web',
            lastTopic: 'services'
          });
          return getServiceDetails('web');
        }
        if (lastMsg.includes('mobile') && lowerResolved.includes('mobile')) {
          setConversationContext({ 
            ...conversationContext, 
            lastService: 'mobile',
            lastTopic: 'services'
          });
          return getServiceDetails('mobile');
        }
        if ((lastMsg.includes('ai') || lastMsg.includes('ml')) && (lowerResolved.includes('ai') || lowerResolved.includes('ml'))) {
        setConversationContext(prev => ({ 
          ...prev, 
          lastService: 'ai',
          lastTopic: 'services'
        }));
          return getServiceDetails('ai');
        }
      }
    }
    
    // Handle "can you" or "can I" questions
    if (lowerMessage.match(/^can (you|i|we)/)) {
      if (lowerMessage.match(/can (you|i) (help|assist|guide)/)) {
        return {
          text: `Absolutely! I'm here to help. What would you like assistance with?\n\n• Understanding our services\n• Submitting project requirements\n• Scheduling a consultation\n• Any other questions\n\nJust let me know how I can help!`,
          hasLinks: false
        };
      }
      if (lowerMessage.match(/can (you|i) (get|have|receive)/)) {
        if (lowerMessage.match(/quote|pricing|price|cost/)) {
          return {
            text: `Yes, absolutely! For pricing information, I can help you:\n\n1. Submit your project requirements\n2. Schedule a consultation to discuss your needs\n3. Get general pricing information\n\nWhich option would you prefer?`,
            hasLinks: false
          };
        }
      }
    }
    
    // Handle "I need" or "I want" statements
    if (lowerMessage.match(/^(i need|i want|i'm looking for|i require|i'd like)/)) {
      // First, try to map service variations
      const serviceKey = mapServiceVariation(userMessage);
      
      if (serviceKey === 'web' || lowerMessage.match(/website|web app|web development|web site/)) {
        setConversationContext(prev => ({ 
          ...prev, 
          lastService: 'web',
          lastTopic: 'services',
          awaitingResponse: true,
          lastQuestion: 'discuss_project'
        }));
        return {
          text: `Great! We build custom web applications using React, Next.js, Vue.js, and Node.js.\n\nHow would you like to proceed?\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp`,
          hasLinks: false
        };
      }
      if (serviceKey === 'mobile' || lowerMessage.match(/mobile app|app development|ios app|android app|app services|app service/)) {
        setConversationContext(prev => ({ 
          ...prev, 
          lastService: 'mobile',
          lastTopic: 'services',
          awaitingResponse: true,
          lastQuestion: 'discuss_project'
        }));
        return {
          text: `Excellent! We develop native and cross-platform mobile apps using React Native, Flutter, and native technologies.\n\nHow would you like to proceed?\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp`,
          hasLinks: false
        };
      }
      if (serviceKey === 'ai' || lowerMessage.match(/ai|ml|machine learning|artificial intelligence|chatbot/)) {
        setConversationContext(prev => ({ 
          ...prev, 
          lastService: 'ai',
          lastTopic: 'services',
          awaitingResponse: true,
          lastQuestion: 'discuss_project'
        }));
        return {
          text: `Perfect! We provide AI/ML solutions including custom ML models, NLP, computer vision, and AI chatbots.\n\nHow would you like to proceed?\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp`,
          hasLinks: false
        };
      }
      if (serviceKey === 'cloud' || lowerMessage.match(/cloud|devops|aws|azure|gcp|infrastructure/)) {
        setConversationContext(prev => ({ 
          ...prev, 
          lastService: 'cloud',
          lastTopic: 'services',
          awaitingResponse: true,
          lastQuestion: 'discuss_project'
        }));
        return {
          text: `Great! We provide cloud infrastructure and DevOps solutions (AWS, Azure, GCP, Docker, Kubernetes).\n\nHow would you like to proceed?\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp`,
          hasLinks: false
        };
      }
      if (serviceKey === 'design' || lowerMessage.match(/ui|ux|design|user interface|user experience/)) {
        setConversationContext(prev => ({ 
          ...prev, 
          lastService: 'design',
          lastTopic: 'services',
          awaitingResponse: true,
          lastQuestion: 'discuss_project'
        }));
        return {
          text: `Excellent! We create user-centered designs with research, wireframing, and prototyping.\n\nHow would you like to proceed?\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp`,
          hasLinks: false
        };
      }
      if (serviceKey === 'backend' || lowerMessage.match(/backend|api|database|server|microservices/)) {
        setConversationContext(prev => ({ 
          ...prev, 
          lastService: 'backend',
          lastTopic: 'services',
          awaitingResponse: true,
          lastQuestion: 'discuss_project'
        }));
        return {
          text: `Perfect! We build robust backend systems with microservices, APIs, and database optimization.\n\nHow would you like to proceed?\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp`,
          hasLinks: false
        };
      }
      if (serviceKey === 'automation' || lowerMessage.match(/workflow|automation|zapier|n8n|make|integromat|integration|process automation/)) {
        setConversationContext(prev => ({ 
          ...prev, 
          lastService: 'automation',
          lastTopic: 'services',
          awaitingResponse: true,
          lastQuestion: 'discuss_project'
        }));
        return {
          text: `Workflow Automation & Integrations Services:\n\nWe design, build, and optimize automated workflows using:\n• n8n Automation: Self-hosted workflow automation for full control and security\n• Zapier Integrations: Fast and reliable integrations between popular SaaS tools\n• Make Automation: Advanced visual workflows with complex logic and data transformation\n• Features: API integrations, webhooks, scheduled jobs, error handling\n• Use Cases: CRM automation, marketing workflows, data pipelines, e-commerce integration\n\nOur automation solutions:\n✓ Connect your tools and eliminate manual work\n✓ Improve operational efficiency\n✓ Secure and scalable architectures\n✓ Long-term support and optimization\n\nTimeline: 1-4 weeks for typical automation projects\nPricing: Starting at $18/hour\n\nHow would you like to proceed?\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp`,
          hasLinks: false
        };
      }
      if (lowerMessage.match(/quote|pricing|price|cost|estimate/)) {
        return {
          text: `Our Pricing:\n\n• Hourly: $18/hour (General development)\n• Monthly: $2560/month (Dedicated developer)\n• AI Development: $25/hour\n• Cloud Services: $20/hour\n\nFor more information:\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp\n\nEmail: ${contactInfo.email}\nPhone: ${contactInfo.phone}\nWhatsApp: Click to chat\nBook a Meeting: Click to schedule`,
          hasLinks: true
        };
      }
    }
    
    // Handle "how" questions
    if (lowerMessage.match(/^how (do|does|can|will|much|long|many)/)) {
      if (lowerMessage.match(/how (much|cost|price)/)) {
        setConversationContext(prev => ({ ...prev, lastTopic: 'pricing' }));
      }
      if (lowerMessage.match(/how long|how much time|how many (weeks|months|days)/)) {
        setConversationContext(prev => ({ ...prev, lastTopic: 'timeline' }));
      }
    }
    
    // Greetings - Action-oriented
    if (lowerMessage.match(/\b(hi|hello|hey|greetings|good morning|good afternoon|good evening)\b/)) {
      setConversationContext({ 
        lastTopic: null, 
        lastService: null, 
        awaitingResponse: false, 
        lastQuestion: null,
        lastOptions: null,
        conversationHistory: [],
        lastBotMessage: null,
        discussedTopics: []
      });
      setShowQuickActions(true);
      return {
        text: "Hello! 👋 How can I help you today?\n\n1. Submit project requirements\n2. Book a meeting\n3. Chat on WhatsApp\n4. Learn about services\n\nWhat would you like to do? (Tell me the number or name)",
        hasLinks: false
      };
    }
    
    // Handle "thanks", "thank you", etc.
    if (lowerMessage.match(/\b(thanks|thank you|thank|appreciate|grateful)\b/)) {
      return {
        text: `You're welcome! I'm glad I could help. Is there anything else you'd like to know about our services, pricing, or how we can assist with your project?`,
        hasLinks: false
      };
    }
    
    // Handle "bye", "goodbye", etc.
    if (lowerMessage.match(/\b(bye|goodbye|see you|farewell|have a good day)\b/)) {
      return {
        text: `Thank you for chatting with us! Feel free to come back anytime if you have more questions. Have a great day! 👋`,
        hasLinks: false
      };
    }
    
    // Handle clarification requests
    if (lowerMessage.match(/\b(what do you mean|i don't understand|can you clarify|explain|what does that mean)\b/)) {
      if (context?.lastBotMessage) {
        return {
          text: `Let me clarify. ${context.lastBotMessage}\n\nIs there a specific part you'd like me to explain in more detail?`,
          hasLinks: false
        };
      }
      return {
        text: `I'd be happy to clarify! Could you please let me know what specifically you'd like me to explain?`,
        hasLinks: false
      };
    }
    
    // Handle topic switching
    if (lowerMessage.match(/\b(actually|by the way|also|one more thing|another question|different topic)\b/)) {
      return {
        text: `Sure! I'm here to help. What would you like to know about?\n\n• Our services\n• Pricing\n• Technologies\n• Portfolio\n• Contact information\n\nOr feel free to ask anything else!`,
        hasLinks: false
      };
    }
    
    // Services - General - Simplified
    if (lowerMessage.match(/\b(service|services|what do you do|what services)\b/) && !lowerMessage.match(/\b(web|mobile|ai|ml|cloud|devops|ui|ux|backend|design)\b/)) {
      const servicesListText = `We offer:\n\n1. Web Development - React, Next.js, Vue.js\n2. Mobile App Development - iOS, Android, React Native, Flutter\n3. AI/ML Services - Custom ML models, NLP, chatbots\n4. Cloud & DevOps - AWS, Azure, GCP, Docker, Kubernetes\n5. UI/UX Design - User research, wireframing, prototyping\n6. Backend Development - Microservices, APIs, databases\n7. Workflow Automation - n8n, Zapier, Make integrations\n\nWhich service interests you? (Tell me the number or name)\n\nOr would you like to:\nA. Submit project requirements\nB. Book a meeting\nC. Chat on WhatsApp\n\n(Tell me the service number 1-7, or action letter A-C)`;
      
      setConversationContext(prev => ({ 
        ...prev,
        lastTopic: 'services', 
        lastService: null, 
        awaitingResponse: true, 
        lastQuestion: 'service_details',
        lastBotMessage: servicesListText
      }));
      
      return {
        text: servicesListText,
        hasLinks: false
      };
    }
    
    // Web Development
    if (lowerMessage.match(/\b(web development|web app|website|react|next\.js|vue|frontend)\b/)) {
      setConversationContext({ 
        lastTopic: 'services', 
        lastService: 'web', 
        awaitingResponse: true, 
        lastQuestion: 'discuss_project' 
      });
      return {
        text: `Web Development Services:\n\nWe build custom web applications using modern technologies:\n• Frontend: React, Next.js, Vue.js, Nuxt.js, TypeScript\n• Backend: Node.js, Python, Java, Go, PHP, Ruby\n• Features: Responsive design, SEO optimization, Progressive Web Apps (PWA)\n• Scalability: Microservices architecture, cloud deployment\n• Performance: Fast loading, optimized code, CDN integration\n\nOur web solutions are:\n✓ Fully responsive and mobile-friendly\n✓ SEO-optimized for better search rankings\n✓ Scalable to handle growing traffic\n✓ Secure with best practices\n\nPricing: Starting at $18/hour or $2560/month for dedicated developers.\n\nHow would you like to proceed?\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp`,
        hasLinks: false
      };
    }
    
    // Mobile App Development
    if (lowerMessage.match(/\b(mobile app|mobile development|ios|android|react native|flutter|app development)\b/)) {
      setConversationContext({ 
        lastTopic: 'services', 
        lastService: 'mobile', 
        awaitingResponse: true, 
        lastQuestion: 'discuss_project' 
      });
      return {
        text: `Mobile App Development Services:\n\nWe develop native and cross-platform mobile applications:\n• Platforms: iOS, Android, Cross-platform\n• Technologies: React Native, Flutter, Native iOS (Swift), Native Android (Kotlin)\n• Features: Push notifications, in-app purchases, offline capabilities\n• App Store: Complete submission and optimization\n\nOur mobile apps include:\n✓ Native performance and user experience\n✓ Cross-platform compatibility (iOS & Android)\n✓ App store optimization and deployment\n✓ Regular updates and maintenance\n\nTimeline: 6-12 weeks for typical mobile apps\nPricing: Starting at $18/hour or custom project-based pricing\n\nHow would you like to proceed?\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp`,
        hasLinks: false
      };
    }
    
    // AI/ML Services
    if (lowerMessage.match(/\b(ai|artificial intelligence|machine learning|ml|chatbot|nlp|computer vision|deep learning|data science|predictive analytics)\b/)) {
      setConversationContext({ 
        lastTopic: 'services', 
        lastService: 'ai', 
        awaitingResponse: true, 
        lastQuestion: 'discuss_project' 
      });
      return {
        text: `AI/ML Services:\n\nWe provide comprehensive AI and Machine Learning solutions:\n• Machine Learning: Custom ML models, model training, deployment\n• Natural Language Processing (NLP): Text analysis, sentiment analysis, language models\n• Computer Vision: Image recognition, object detection, video analysis\n• Predictive Analytics: Forecasting, trend analysis, data-driven insights\n• AI Chatbots: Intelligent conversational agents, customer support automation\n• Deep Learning: Neural networks, deep learning models\n• Data Science: Data analysis, visualization, business intelligence\n\nOur AI solutions:\n✓ Custom AI models tailored to your business\n✓ Integration with existing systems\n✓ Real-time processing and analytics\n✓ Continuous learning and improvement\n\nPricing: Starting at $25/hour for AI development\n\nHow would you like to proceed?\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp`,
        hasLinks: false
      };
    }
    
    // Cloud & DevOps
    if (lowerMessage.match(/\b(cloud|devops|aws|azure|gcp|docker|kubernetes|ci\/cd|infrastructure|deployment)\b/)) {
      setConversationContext(prev => ({ 
        ...prev, 
        lastTopic: 'services', 
        lastService: 'cloud', 
        awaitingResponse: true, 
        lastQuestion: 'discuss_project' 
      }));
      return {
        text: `Cloud & DevOps Services:\n\nWe provide cloud infrastructure and DevOps solutions:\n• Cloud Platforms: AWS, Azure, Google Cloud Platform (GCP)\n• Containerization: Docker, Kubernetes orchestration\n• CI/CD: Automated pipelines, continuous integration/deployment\n• Infrastructure: Server setup, scaling, load balancing\n• Monitoring: Performance monitoring, logging, alerting\n• Security: Cloud security, compliance, backup solutions\n\nOur cloud services:\n✓ Scalable and reliable infrastructure\n✓ Automated deployment pipelines\n✓ 24/7 monitoring and support\n✓ Cost optimization and resource management\n\nPricing: Starting at $20/hour for cloud services\n\nHow would you like to proceed?\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp`,
        hasLinks: false
      };
    }
    
    // UI/UX Design
    if (lowerMessage.match(/\b(ui|ux|design|user interface|user experience|wireframe|prototype|usability)\b/)) {
      setConversationContext(prev => ({ 
        ...prev, 
        lastTopic: 'services', 
        lastService: 'design', 
        awaitingResponse: true, 
        lastQuestion: 'discuss_project' 
      }));
      return {
        text: `UI/UX Design Services:\n\nWe create user-centered designs that enhance user experience:\n• User Research: User interviews, surveys, persona development\n• Wireframing: Low and high-fidelity wireframes\n• Prototyping: Interactive prototypes, user testing\n• Design Systems: Consistent design language, component libraries\n• Usability Testing: User feedback, iterative improvements\n• Responsive Design: Mobile-first, tablet, desktop layouts\n\nOur design process:\n✓ User-centered approach\n✓ Modern and intuitive interfaces\n✓ Accessibility compliance\n✓ Brand consistency\n\nHow would you like to proceed?\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp`,
        hasLinks: false
      };
    }
    
    // Backend Development
    if (lowerMessage.match(/\b(backend|api|database|server|microservices|rest api|graphql)\b/)) {
      setConversationContext(prev => ({ 
        ...prev, 
        lastTopic: 'services', 
        lastService: 'backend', 
        awaitingResponse: true, 
        lastQuestion: 'discuss_project' 
      }));
      return {
        text: `Backend Development Services:\n\nWe build robust and scalable backend systems:\n• Architecture: Microservices, monolithic, serverless\n• APIs: RESTful APIs, GraphQL, WebSocket\n• Databases: SQL (PostgreSQL, MySQL), NoSQL (MongoDB, Redis)\n• Technologies: Node.js, Python, Java, Go, PHP, Ruby\n• Features: Authentication, authorization, real-time communication\n• Performance: Optimization, caching, load balancing\n\nOur backend solutions:\n✓ Scalable and maintainable architecture\n✓ Secure APIs with authentication\n✓ Database optimization\n✓ Real-time capabilities\n\nHow would you like to proceed?\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp`,
        hasLinks: false
      };
    }
    
    // Pricing - Simplified
    if (lowerMessage.match(/\b(price|pricing|cost|how much|quote|budget)\b/)) {
      return {
        text: `Our Pricing:\n\n• Hourly: $18/hour (General development)\n• Monthly: $2560/month (Dedicated developer)\n• AI Development: $25/hour\n• Cloud Services: $20/hour\n\nFor a detailed quote:\n\nA. Submit your project requirements\nB. Schedule a consultation\nC. Chat on WhatsApp\n\nEmail: ${contactInfo.email}\nPhone: ${contactInfo.phone}\nWhatsApp: Click to chat\nBook a Meeting: Click to schedule`,
        hasLinks: true
      };
    }
    
    // Contact - Include WhatsApp option
    if (lowerMessage.match(/\b(contact|email|phone|reach|get in touch|whatsapp|chat)\b/)) {
      if (lowerMessage.match(/\b(whatsapp|chat)\b/)) {
        if (contactInfo.whatsapp) {
          window.open(contactInfo.whatsapp, '_blank', 'noopener,noreferrer');
        }
        return {
          text: `Great! I've opened WhatsApp for you. You can chat directly with our team.\n\nWhatsApp: Click to chat`,
          hasLinks: true
        };
      }
      return {
        text: `You can reach us through:\n\nEmail: ${contactInfo.email}\nPhone: ${contactInfo.phone}\nWhatsApp: Click to chat\nBook a Meeting: Click to schedule\n\nOur office hours are Mon-Fri, 9AM-6PM.`,
        hasLinks: true
      };
    }
    
    // Company info
    if (lowerMessage.match(/\b(about|company|who are you|octalchip|experience|team)\b/)) {
      return {
        text: `About Octalchip:\n\nOctalchip is a technology company empowering businesses with cutting-edge digital solutions. We specialize in web development, mobile app development, AI/ML services, cloud infrastructure, and digital transformation.\n\nOur Expertise:\n• 25+ agile-enabled employees\n• 10+ successfully delivered projects\n• 3+ years of industry experience\n• Serving clients globally (United States, United Kingdom, Germany, France, Netherlands, Canada, Australia, UAE, Singapore, India, Saudi Arabia, Mexico, Brazil, Japan, South Korea, Poland, Romania, Bulgaria)\n\nWhat We Do:\n✓ Custom software development\n✓ Digital transformation consulting\n✓ AI-powered solutions\n✓ Cloud infrastructure setup\n✓ Ongoing support and maintenance\n\nOur Approach:\n• Agile methodology\n• Client-focused solutions\n• Quality-driven development\n• Transparent communication\n• Post-launch support\n\nVisit our About page to learn more about our team and values!`,
        hasLinks: false
      };
    }
    
    // Location
    if (lowerMessage.match(/\b(location|address|where|office)\b/)) {
      return {
        text: `Our head office is located at:\n\n${contactInfo.location}`,
        hasLinks: true
      };
    }
    
    // Portfolio/Projects
    if (lowerMessage.match(/\b(portfolio|projects|work|previous work|clients)\b/) && !lowerMessage.match(/\b(examples|case studies|see examples|show examples|view examples)\b/)) {
      setConversationContext(prev => ({ 
        ...prev, 
        lastTopic: 'portfolio',
        awaitingResponse: true,
        lastQuestion: 'portfolio_examples'
      }));
      return {
        text: `Our Portfolio:\n\nWe've successfully delivered 10+ projects across various industries including:\n\n• FinTech: Payment platforms, banking solutions, investment platforms\n• E-commerce: Online stores, marketplace platforms\n• Healthcare: Medical apps, patient management systems\n• Logistics: Fleet management, tracking systems\n• Manufacturing: IoT solutions, automation systems\n• Real Estate: Property management platforms\n\nProject Highlights:\n✓ Scalable and high-performance applications\n✓ Modern tech stack implementations\n✓ Successful deployments and launches\n✓ Client satisfaction and retention\n\nYou can check out our blog section to see detailed case studies of our work. Each case study includes:\n• Project overview and challenges\n• Solutions implemented\n• Technologies used\n• Results and impact\n\nWould you like to discuss a specific project requirement or see examples in a particular industry?`,
        hasLinks: false
      };
    }
    
    // Support/Maintenance
    if (lowerMessage.match(/\b(support|maintenance|updates|bug|fix|after launch|post launch|ongoing)\b/)) {
      return {
        text: `Support & Maintenance Services:\n\nWe offer comprehensive post-launch support to ensure your application continues to perform optimally:\n\n• Bug Fixes: Quick resolution of issues\n• Updates: Regular feature updates and enhancements\n• Maintenance: System monitoring and optimization\n• Security: Security patches and updates\n• Performance: Performance monitoring and optimization\n• Technical Support: 24/7 support availability\n\nOur Support Packages:\n✓ Regular maintenance plans\n✓ Emergency support for critical issues\n✓ Feature enhancement services\n✓ Performance optimization\n✓ Security updates\n\nWe ensure your application remains:\n• Up-to-date with latest technologies\n• Secure and protected\n• Optimized for performance\n• Scalable for growth\n\nWould you like to discuss a support package for your project?`,
        hasLinks: false
      };
    }
    
    // Timeline/Duration
    if (lowerMessage.match(/\b(timeline|duration|how long|time|delivery|deadline|when|schedule)\b/)) {
      return {
        text: `Project Timelines:\n\nProject duration depends on complexity, features, and requirements:\n\n• Simple Web Applications: 2-4 weeks\n  Includes: Basic website, landing pages, simple web apps\n\n• Mobile Applications: 6-12 weeks\n  Includes: Native or cross-platform mobile apps\n\n• Complex Web Applications: 4-8 weeks\n  Includes: E-commerce, dashboards, complex features\n\n• Enterprise Solutions: 3-6 months\n  Includes: Large-scale systems, multiple integrations\n\n• AI/ML Projects: 4-12 weeks\n  Includes: Model development, training, integration\n\nFactors Affecting Timeline:\n• Project scope and features\n• Number of integrations\n• Design complexity\n• Testing requirements\n• Client feedback cycles\n\nWe provide detailed project timelines during consultation based on your specific requirements.\n\nWould you like to discuss your project timeline?`,
        hasLinks: false
      };
    }
    
    // Technology stack
    if (lowerMessage.match(/\b(technology|tech|stack|technologies|tools|framework|programming|language)\b/)) {
      return {
        text: `Our Technology Stack:\n\nFrontend Technologies:\n• React, Next.js, Vue.js, Nuxt.js, Svelte\n• TypeScript, JavaScript (ES6+)\n• Tailwind CSS, Material-UI, Bootstrap\n• Redux, Zustand for state management\n\nBackend Technologies:\n• Node.js, Express.js, Nest.js\n• Python (Django, Flask, FastAPI)\n• Java (Spring Boot)\n• Go, PHP, Ruby on Rails\n• GraphQL, REST APIs\n\nMobile Development:\n• React Native (Cross-platform)\n• Flutter (Cross-platform)\n• Native iOS (Swift)\n• Native Android (Kotlin)\n\nAI/ML Technologies:\n• TensorFlow, PyTorch\n• Scikit-learn, Pandas, NumPy\n• OpenAI API, LangChain\n• Computer Vision libraries\n\nCloud & DevOps:\n• AWS (EC2, S3, Lambda, RDS)\n• Azure, Google Cloud Platform\n• Docker, Kubernetes\n• Terraform, Ansible\n• CI/CD: GitHub Actions, Jenkins\n\nDatabases:\n• PostgreSQL, MySQL, MongoDB\n• Redis, Elasticsearch\n• Firebase, Supabase\n\nCheck our Skills section for the complete list!`,
        hasLinks: false
      };
    }
    
    // Book meeting/consultation - Direct action
    if (lowerMessage.match(/\b(book|meeting|schedule|consultation|appointment|calendly|cal\.com|call)\b/)) {
      window.open(contactInfo.bookMeeting, '_blank', 'noopener,noreferrer');
      return {
        text: `Perfect! I've opened the booking page for you. Schedule a free 30-minute consultation.\n\nBook a Meeting: Click to schedule`,
        hasLinks: true
      };
    }
    
    // Submit project requirements - Direct action
    if (lowerMessage.match(/\b(submit.*requirements|project requirements|project details|project info|tell.*project|share.*project|describe.*project|my project|need.*project|want.*submit|requirements)\b/)) {
      // Start collecting requirements
      setIsCollectingRequirements(true);
      setRequirementsStep(0);
      setRequirementsForm({ name: '', email: '', phone: '', projectType: '', requirements: '' });
      return {
        text: `Great! Let me collect your project requirements.\n\nStep 1 of 5: What's your name?\n\n(You can type "cancel" at any time to stop)`,
        hasLinks: false
      };
    }
    
    // Handle ambiguous or unclear messages by checking conversation history
    if (context?.conversationHistory && context.conversationHistory.length > 0) {
      const lastExchange = context.conversationHistory[context.conversationHistory.length - 1];
      const lastBotMsg = lastExchange.bot.toLowerCase();
      const lastUserMsg = lastExchange.user.toLowerCase();
      
      // If user message is very short or unclear, try to infer from context
      if (userMessage.trim().length < 10 && !lowerMessage.match(/^\d+$/)) {
        // Check if it might be a follow-up to last message
        if (lastBotMsg.includes('would you like') || lastBotMsg.includes('which') || lastBotMsg.includes('prefer')) {
          // Might be answering a question - try to match with options
          if (lowerMessage.match(/\b(first|1st|one)\b/)) {
            // User wants first option
            if (lastBotMsg.includes('1.')) {
              // Extract first option and handle it
              const firstOptionMatch = lastBotMsg.match(/1\.\s*([^\n]+)/);
              if (firstOptionMatch) {
                const firstOption = firstOptionMatch[1].toLowerCase();
                if (firstOption.includes('submit') || firstOption.includes('requirement')) {
                  setIsCollectingRequirements(true);
                  setRequirementsStep(0);
                  setRequirementsForm({ name: '', email: '', phone: '', projectType: '', requirements: '' });
                  return {
                    text: `Perfect! Let me collect your project requirements. To get started, please provide your name:\n\n(You can type "cancel" at any time to stop)`,
                    hasLinks: false
                  };
                }
              }
            }
          }
        }
      }
      
      // If user asks about something mentioned in last bot message
      if (lastBotMsg.includes('web development') && (lowerMessage.includes('web') || lowerMessage.includes('website'))) {
        setConversationContext(prev => ({ 
          ...prev, 
          lastService: 'web',
          lastTopic: 'services'
        }));
        return getServiceDetails('web');
      }
      if (lastBotMsg.includes('mobile') && lowerMessage.includes('mobile')) {
        setConversationContext(prev => ({ 
          ...prev, 
          lastService: 'mobile',
          lastTopic: 'services'
        }));
        return getServiceDetails('mobile');
      }
      if ((lastBotMsg.includes('workflow') || lastBotMsg.includes('automation')) && (lowerMessage.includes('workflow') || lowerMessage.includes('automation') || lowerMessage.includes('zapier') || lowerMessage.includes('n8n') || lowerMessage.includes('make'))) {
        setConversationContext(prev => ({ 
          ...prev, 
          lastService: 'automation',
          lastTopic: 'services'
        }));
        return getServiceDetails('automation');
      }
      
      // Handle "and" or "also" - user wants to know more about something else
      if (lowerMessage.match(/^(and|also|plus|additionally)/)) {
        if (context?.discussedTopics && context.discussedTopics.length > 0) {
          const lastTopic = context.discussedTopics[context.discussedTopics.length - 1];
          if (lastTopic === 'web' && !lowerMessage.includes('web')) {
            return {
              text: `Great! What else would you like to know about? You've already asked about web development. Would you like to know about:\n\n• Mobile app development\n• AI/ML services\n• Pricing\n• Our portfolio\n• Something else\n\nJust let me know!`,
              hasLinks: false
            };
          }
        }
      }
    }
    
    // Handle "what about" or "how about" - asking about alternatives
    if (lowerMessage.match(/^(what about|how about|what if|how about if)/)) {
      if (context?.lastService) {
        // User asking about alternative to last service
        if (context.lastService === 'web') {
          return {
            text: `Great question! If you're considering alternatives to web development, you might also be interested in:\n\n• Mobile App Development - For native mobile experiences\n• Progressive Web Apps (PWA) - Web apps that work like mobile apps\n• Hybrid solutions - Combining web and mobile\n\nWould you like to know more about any of these options?`,
            hasLinks: false
          };
        }
      }
      return {
        text: `I'd be happy to discuss alternatives! What specifically are you considering? Feel free to tell me more about what you're looking for.`,
        hasLinks: false
      };
    }
    
    // Default response - Action-oriented with better handling
    // Check if user might be trying to select an option but context was lost
    if (context?.lastOptions && /^\d+$/.test(userMessage.trim())) {
      const optionNum = parseInt(userMessage.trim());
      if (optionNum >= 1 && optionNum <= context.lastOptions.length) {
        // Re-process as option selection
        const selectedAction = context.lastOptions[optionNum - 1];
        setConversationContext(prev => ({ 
          ...prev, 
          awaitingResponse: false, 
          lastOptions: null 
        }));
        
        if (selectedAction.action === 'submit_requirements') {
          setIsCollectingRequirements(true);
          setRequirementsStep(0);
          setRequirementsForm({ name: '', email: '', phone: '', projectType: '', requirements: '' });
          return {
            text: `Perfect! Let me collect your project requirements.\n\nStep 1 of 5: What's your name?\n\n(You can type "cancel" at any time to stop)`,
            hasLinks: false
          };
        }
        // Handle other actions similarly if needed
      }
    }
    
    // If user input is very short and unclear, provide helpful guidance
    if (userMessage.trim().length < 3) {
      const defaultResponseText = `I didn't quite understand that. How can I help you?\n\n1. Submit your project requirements\n2. Book a meeting\n3. Chat on WhatsApp\n4. Learn about our services\n\nTell me the number or name`;
      setShowQuickActions(true);
      setConversationContext(prev => ({
        ...prev,
        awaitingResponse: true,
        lastOptions: null,
        lastQuestion: null,
        lastBotMessage: defaultResponseText
      }));
      return {
        text: defaultResponseText,
        hasLinks: false
      };
    }
    
    const defaultResponseText = `I'm here to help! How would you like to proceed?\n\n1. Submit your project requirements\n2. Book a meeting\n3. Chat on WhatsApp\n4. Learn about our services\n\nTell me the number or name\n\nEmail: ${contactInfo.email}\nPhone: ${contactInfo.phone}\nWhatsApp: Click to chat\nBook a Meeting: Click to schedule`;
    setShowQuickActions(true);
    setConversationContext(prev => ({
      ...prev,
      awaitingResponse: true,
      lastOptions: null,
      lastQuestion: null,
      lastBotMessage: defaultResponseText
    }));
    return {
      text: defaultResponseText,
      hasLinks: true
    };
  };

  // Render message with clickable links
  const renderMessage = (text, hasLinks = false) => {
    if (!hasLinks) {
      return <p className="text-xs sm:text-sm whitespace-pre-line break-words">{text}</p>;
    }

    // Split text by lines and process each line
    const lines = text.split('\n');
    
    return (
      <div className="text-xs sm:text-sm whitespace-pre-line space-y-1.5 sm:space-y-2 break-words">
        {lines.map((line, index) => {
          // Handle empty lines
          if (!line.trim()) {
            return <p key={index} className="h-2">&nbsp;</p>;
          }
          
          // Priority order: Check for contact info in order of specificity
          // This ensures more specific patterns are matched first
          
          // Skip rendering if this line should be filtered (duplicate map link)
          if (line.includes('Click to view on Google Maps') && !line.includes(contactInfo.location)) {
            const previousLines = lines.slice(0, index);
            const hasAddressLine = previousLines.some(prevLine => 
              prevLine && (prevLine.includes('Octalchip') || prevLine.includes('Opera point') || prevLine.includes(contactInfo.location))
            );
            if (hasAddressLine) {
              return null;
            }
          }

          // Check if line contains email
          if (line.includes(contactInfo.email)) {
            const parts = line.split(contactInfo.email);
            const beforeText = parts[0].trim();
            const afterText = parts[1] || '';
            return (
              <p key={index} className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <Icon name="Mail" size={14} className="sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                {beforeText && <span className="text-text-primary break-words">{beforeText}</span>}
                <a 
                  href={contactInfo.emailLink}
                  className="text-primary hover:underline font-medium break-all"
                  onClick={(e) => e.stopPropagation()}
                >
                  {contactInfo.email}
                </a>
                {afterText && <span className="text-text-primary break-words">{afterText}</span>}
              </p>
            );
          }
          
          // Check if line contains phone
          if (contactInfo.phone && line.includes(contactInfo.phone)) {
            const parts = line.split(contactInfo.phone);
            const beforeText = parts[0].trim();
            const afterText = parts[1] || '';
            return (
              <p key={index} className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <Icon name="Phone" size={14} className="sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                {beforeText && <span className="text-text-primary break-words">{beforeText}</span>}
                <a 
                  href={contactInfo.phoneLink}
                  className="text-primary hover:underline font-medium"
                  onClick={(e) => e.stopPropagation()}
                >
                  {contactInfo.phone}
                </a>
                {afterText && <span className="text-text-primary break-words">{afterText}</span>}
              </p>
            );
          }
          
          // Check if line contains WhatsApp link pattern (more specific to avoid matching lines that just mention WhatsApp)
          if ((line.includes('WhatsApp:') && line.includes('Click to chat')) || 
              (line.includes('Click to chat') && !line.includes('opened WhatsApp'))) {
            let beforeText = '';
            let whatsAppText = '';
            let linkText = '';
            let afterText = '';
            
            // Handle "WhatsApp: Click to chat" or "WhatsApp: Click to chat directly"
            if (line.includes('WhatsApp: Click to chat directly')) {
              const parts = line.split('WhatsApp: Click to chat directly');
              beforeText = parts[0].trim();
              whatsAppText = 'WhatsApp:';
              linkText = 'Click to chat directly';
              afterText = parts[1] || '';
            } else if (line.includes('WhatsApp: Click to chat')) {
              const parts = line.split('WhatsApp: Click to chat');
              beforeText = parts[0].trim();
              whatsAppText = 'WhatsApp:';
              linkText = 'Click to chat';
              afterText = parts[1] || '';
            } else if (line.includes('Click to chat directly')) {
              const parts = line.split('Click to chat directly');
              beforeText = parts[0].trim();
              linkText = 'Click to chat directly';
              afterText = parts[1] || '';
            } else if (line.includes('Click to chat')) {
              const parts = line.split('Click to chat');
              beforeText = parts[0].trim();
              linkText = 'Click to chat';
              afterText = parts[1] || '';
            }
            
            return (
              <p key={index} className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <Icon name="MessageCircle" size={14} className="sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                {beforeText && <span className="text-text-primary break-words">{beforeText}</span>}
                {whatsAppText && <span className="text-text-primary">{whatsAppText}</span>}
                <a 
                  href={contactInfo.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline font-medium break-words"
                  onClick={(e) => e.stopPropagation()}
                >
                  {linkText}
                </a>
                {afterText && <span className="text-text-primary break-words">{afterText}</span>}
              </p>
            );
          }
          
          // Check if line is "Location:" followed by address - check this FIRST
          if (line.trim().startsWith('Location:')) {
            const parts = line.split('Location:');
            const address = parts[1]?.trim() || '';
            // Check if address contains the actual location
            if (address && (address.includes('Octalchip') || address.includes('Opera point') || address.includes(contactInfo.location))) {
              return (
                <p key={index} className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                  <Icon name="MapPin" size={14} className="sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                  <span className="text-text-primary">Location:</span>
                  <span className="text-text-primary break-words">{address}</span>
                  <a 
                    href={contactInfo.locationLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline font-medium"
                    onClick={(e) => e.stopPropagation()}
                  >
                    (View on Map)
                  </a>
                </p>
              );
            }
            // If just "Location:" without address
            return (
              <p key={index} className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <Icon name="MapPin" size={14} className="sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                <span className="text-text-primary break-words">{line}</span>
              </p>
            );
          }
          
          // Check if line contains location address (for location response) - check this AFTER Location: check
          // Only match if it's the actual address, not if it contains "Google Maps" or "Click" or "Location:"
          if ((line.includes('Octalchip, 404') || line.includes('Opera point') || line.includes(contactInfo.location)) 
              && !line.includes('Google Maps') && !line.includes('Click') && !line.includes('Location:')) {
            // Check if next line is also part of the address
            const nextLine = index < lines.length - 1 ? lines[index + 1] : '';
            const isMultiLineAddress = nextLine && (
              nextLine.includes('gujarat') || 
              nextLine.includes('india') || 
              nextLine.includes('360022') ||
              nextLine.includes(contactInfo.location.split(',')[contactInfo.location.split(',').length - 1]?.trim())
            );
            
            // Show icon inline with first line of address - icon should be on same line as text
            return (
              <p key={index} className="flex items-start gap-1.5 sm:gap-2">
                <Icon name="MapPin" size={14} className="sm:w-4 sm:h-4 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-text-primary break-words">{line.trim()}</span>
              </p>
            );
          }
          
          // Check if this is a continuation of address (second line of multi-line address)
          if (index > 0) {
            const prevLine = lines[index - 1];
            if (prevLine && (
              prevLine.includes('Octalchip') || 
              prevLine.includes('Opera point') || 
              prevLine.includes(contactInfo.location.split(',')[0])
            ) && (line.includes('gujarat') || line.includes('india') || line.includes('360022'))) {
              // This is a continuation line, show without icon but add map link at the end
              // Check if this is the last address line
              const nextLine = index < lines.length - 1 ? lines[index + 1] : '';
              const isLastAddressLine = !nextLine || !nextLine.trim() || nextLine.includes('View on Map') || nextLine.includes('Click');
              
              return (
                <p key={index} className="flex items-center gap-1.5 sm:gap-2 pl-5 sm:pl-6">
                  <span className="text-text-primary break-words">{line.trim()}</span>
                  {isLastAddressLine && (
                    <a 
                      href={contactInfo.locationLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline font-medium ml-1 sm:ml-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      (View on Map)
                    </a>
                  )}
                </p>
              );
            }
          }
          
          // Check if line contains "Click to view on Google Maps" - but skip if we already showed address with (View on Map)
          // Only show this if it's a standalone line, not if address was already shown above
          if (line.includes('Click to view on Google Maps') && !line.includes(contactInfo.location)) {
            // Check if previous lines already showed the address - if so, skip this duplicate
            const previousLines = lines.slice(0, index);
            const hasAddressLine = previousLines.some(prevLine => 
              prevLine && (prevLine.includes('Octalchip') || prevLine.includes('Opera point') || prevLine.includes(contactInfo.location))
            );
            if (!hasAddressLine) {
              return (
                <p key={index} className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                  <Icon name="MapPin" size={14} className="sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                  <a 
                    href={contactInfo.locationLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline font-medium break-words"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Click to view on Google Maps
                  </a>
                </p>
              );
            }
            // If address was already shown, don't render this duplicate line
            return null;
          }
          
          // Check if line contains "Google Maps" or "Click" with "view" - but only if not already handled
          if ((line.includes('Google Maps') || (line.includes('Click') && line.includes('view'))) 
              && !line.includes('Click to view on Google Maps') && !line.includes(contactInfo.location)) {
            const parts = line.split('Click');
            const beforeText = parts[0]?.trim() || '';
            const afterText = parts[1]?.replace(/to view on Google Maps\.?/i, '').trim() || '';
            return (
              <p key={index} className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <Icon name="MapPin" size={14} className="sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                {beforeText && <span className="text-text-primary break-words">{beforeText}</span>}
                <a 
                  href={contactInfo.locationLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline font-medium break-words"
                  onClick={(e) => e.stopPropagation()}
                >
                  Click to view on Google Maps
                </a>
                {afterText && <span className="text-text-primary break-words">{afterText}</span>}
              </p>
            );
          }
          
          // Check if line contains "Book a Meeting" - only process lines that actually have "Book a Meeting"
          if (line.includes('Book a Meeting')) {
            let beforeText = '';
            let meetingText = '';
            let linkText = '';
            let afterText = '';
            
            if (line.includes('Book a Meeting: Click to schedule')) {
              const parts = line.split('Book a Meeting: Click to schedule');
              beforeText = parts[0].trim();
              meetingText = 'Book a Meeting:';
              linkText = 'Click to schedule';
              afterText = parts[1] || '';
            } else if (line.includes('Book a Meeting')) {
              const parts = line.split('Book a Meeting');
              beforeText = parts[0].trim();
              meetingText = 'Book a Meeting';
              const remaining = parts[1] || '';
              // Check if remaining text contains "Click to schedule"
              if (remaining.includes('Click to schedule')) {
                const scheduleParts = remaining.split('Click to schedule');
                linkText = 'Click to schedule';
                afterText = scheduleParts[1] || '';
              } else {
                afterText = remaining;
              }
            }
            
            return (
              <p key={index} className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <Icon name="Calendar" size={14} className="sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                {beforeText && <span className="text-text-primary break-words">{beforeText}</span>}
                {meetingText && <span className="text-text-primary">{meetingText}</span>}
                {linkText && (
                  <a 
                    href={contactInfo.bookMeeting}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline font-medium break-words"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {linkText}
                  </a>
                )}
                {afterText && <span className="text-text-primary break-words">{afterText}</span>}
              </p>
            );
          }
          
          // Check if line contains "Click to schedule" but NOT "Book a Meeting" (for other contexts)
          if (line.includes('Click to schedule') && !line.includes('Book a Meeting')) {
            const parts = line.split('Click to schedule');
            const beforeText = parts[0].trim();
            const afterText = parts[1] || '';
            return (
              <p key={index} className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                {beforeText && <span className="text-text-primary break-words">{beforeText}</span>}
                <a 
                  href={contactInfo.bookMeeting}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline font-medium break-words"
                  onClick={(e) => e.stopPropagation()}
                >
                  Click to schedule
                </a>
                {afterText && <span className="text-text-primary break-words">{afterText}</span>}
              </p>
            );
          }
          
          // Regular line - no special formatting needed
          return <p key={index} className="text-text-primary break-words">{line}</p>;
        }).filter(Boolean)}
      </div>
    );
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputValue.trim()) return;

    const userInput = inputValue.trim();
    const userMessage = {
      id: messages.length + 1,
      text: userInput,
      sender: 'user',
      timestamp: new Date()
    };

    // Get the last bot message BEFORE adding the user message to the array
    let lastBotMsg = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].sender === 'bot') {
        lastBotMsg = messages[i].text;
        break;
      }
    }

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Handle cancel requirements collection
    if (isCollectingRequirements && userInput.toLowerCase().match(/\b(cancel|stop|nevermind|no thanks|exit)\b/)) {
      setIsCollectingRequirements(false);
      setRequirementsStep(0);
      setRequirementsForm({ name: '', email: '', phone: '', projectType: '', requirements: '' });
      setIsTyping(false);
      const cancelResponseText = `No problem! I've cancelled the requirements submission. How would you like to proceed?\n\n1. Submit your project requirements\n2. Book a meeting\n3. Chat on WhatsApp\n4. Learn about our services\n\nWhat would you like to do? (Tell me the number or name)`;
      const cancelResponse = {
        id: messages.length + 2,
        text: cancelResponseText,
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, cancelResponse]);
      // Update conversation context to store the message with numbered options
      setConversationContext(prev => ({
        ...prev,
        awaitingResponse: true,
        lastOptions: null,
        lastQuestion: null,
        lastBotMessage: cancelResponseText
      }));
      setShowQuickActions(true); // Show quick actions after cancellation
      return;
    }

    // Handle requirements collection flow
    if (isCollectingRequirements) {
      handleRequirementsCollection(userInput);
      return;
    }

    // Simulate bot thinking time
    setTimeout(() => {
      try {
        // Use the lastBotMsg we already retrieved before adding user message
        const response = getBotResponse(userInput, conversationContext, lastBotMsg);
        const botResponse = {
          id: messages.length + 2,
          text: typeof response === 'string' ? response : response.text,
          hasLinks: typeof response === 'object' ? response.hasLinks : false,
          sender: 'bot',
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, botResponse]);
        
        // Update conversation history
        updateConversationHistory(userInput, response);
        
        // Check if this is a default/unclear response - show quick actions
        const responseText = typeof response === 'string' ? response : response.text;
        const isDefaultResponse = responseText.includes("I'm here to help!") || 
                                  responseText.includes("I didn't quite understand") ||
                                  responseText.includes("I didn't recognize");
        if (isDefaultResponse) {
          setShowQuickActions(true);
        } else {
          // Show quick actions if response asks for input or offers options
          const showsOptions = responseText.includes('1.') || 
                              responseText.includes('•') ||
                              responseText.includes('How would you like');
          setShowQuickActions(showsOptions);
        }
        
        setIsTyping(false);
      } catch (error) {
        console.error('Error processing bot response:', error);
        setIsTyping(false);
        const errorResponse = {
          id: messages.length + 2,
          text: `I apologize, but I encountered an error. Please try again or contact us directly:\n\nEmail: ${contactInfo.email}\nPhone: ${contactInfo.phone}\nWhatsApp: Click to chat`,
          hasLinks: true,
          sender: 'bot',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorResponse]);
        setShowQuickActions(true);
      }
    }, 1000);
  };

  const handleRequirementsCollection = async (userInput) => {
    let nextStep = requirementsStep;
    let updatedForm = { ...requirementsForm };
    let botResponseText = '';

    switch (requirementsStep) {
      case 0: // Name
        // Validate name is not empty
        if (!userInput.trim()) {
          setIsTyping(false);
          const errorResponse = {
            id: messages.length + 2,
            text: 'Please provide your name:',
            sender: 'bot',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, errorResponse]);
          return;
        }
        // Validate name doesn't contain only numbers or special characters
        if (/^[\d\s\W]+$/.test(userInput.trim())) {
          setIsTyping(false);
          const errorResponse = {
            id: messages.length + 2,
            text: 'Please provide a valid name (should contain letters):',
            sender: 'bot',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, errorResponse]);
          return;
        }
        updatedForm.name = userInput.trim();
        setRequirementsForm(updatedForm);
        nextStep = 1;
        botResponseText = `Thanks, ${userInput.trim()}!\n\nStep 2 of 5: What's your email address?`;
        break;
      
      case 1: // Email
        // Enhanced email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const trimmedEmail = userInput.trim().toLowerCase();
        
        if (!trimmedEmail) {
          setIsTyping(false);
          const errorResponse = {
            id: messages.length + 2,
            text: 'Please provide your email address:',
            sender: 'bot',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, errorResponse]);
          return;
        }
        
        if (!emailRegex.test(trimmedEmail)) {
          setIsTyping(false);
          const errorResponse = {
            id: messages.length + 2,
            text: 'Please provide a valid email address (e.g., name@example.com):',
            sender: 'bot',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, errorResponse]);
          return;
        }
        
        updatedForm.email = trimmedEmail;
        setRequirementsForm(updatedForm);
        nextStep = 2;
        botResponseText = `Step 3 of 5: What's your phone number?`;
        break;
      
      case 2: // Phone
        // Validate phone number
        const trimmedPhone = userInput.trim();
        if (!trimmedPhone) {
          setIsTyping(false);
          const errorResponse = {
            id: messages.length + 2,
            text: 'Please provide your phone number:',
            sender: 'bot',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, errorResponse]);
          return;
        }
        
        // Basic phone validation - should contain at least 5 digits
        const phoneDigits = trimmedPhone.replace(/\D/g, '');
        if (phoneDigits.length < 5) {
          setIsTyping(false);
          const errorResponse = {
            id: messages.length + 2,
            text: 'Please provide a valid phone number (at least 5 digits):',
            sender: 'bot',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, errorResponse]);
          return;
        }
        
        updatedForm.phone = trimmedPhone;
        setRequirementsForm(updatedForm);
        nextStep = 3;
        botResponseText = 'Step 4 of 5: What type of project?\n\n1. Web Development\n2. Mobile App Development\n3. AI/ML Services\n4. Cloud & DevOps\n5. UI/UX Design\n6. Backend Development\n7. Other\n\n(You can type the number or name)';
        break;
      
      case 3: // Project Type
        // Handle both number selection and text input
        let projectType = userInput.trim();
        const trimmedProjectInput = projectType.toLowerCase();
        
        // Check if user typed a number (1-7)
        if (/^\d+$/.test(projectType)) {
          const projectNumber = parseInt(projectType);
          const projectTypes = [
            'Web Development',
            'Mobile App Development',
            'AI/ML Services',
            'Cloud & DevOps',
            'UI/UX Design',
            'Backend Development',
            'Other'
          ];
          
          if (projectNumber >= 1 && projectNumber <= 7) {
            projectType = projectTypes[projectNumber - 1];
          } else {
            setIsTyping(false);
            const errorResponse = {
              id: messages.length + 2,
              text: 'Please choose a number between 1 and 7, or type the project type name:',
              sender: 'bot',
              timestamp: new Date()
            };
            setMessages(prev => [...prev, errorResponse]);
            return;
          }
        } else {
          // Map common variations to standard project types
          const projectTypeMap = {
            'web': 'Web Development',
            'website': 'Web Development',
            'web app': 'Web Development',
            'mobile': 'Mobile App Development',
            'app': 'Mobile App Development',
            'mobile app': 'Mobile App Development',
            'ios': 'Mobile App Development',
            'android': 'Mobile App Development',
            'ai': 'AI/ML Services',
            'ml': 'AI/ML Services',
            'machine learning': 'AI/ML Services',
            'artificial intelligence': 'AI/ML Services',
            'cloud': 'Cloud & DevOps',
            'devops': 'Cloud & DevOps',
            'aws': 'Cloud & DevOps',
            'azure': 'Cloud & DevOps',
            'gcp': 'Cloud & DevOps',
            'design': 'UI/UX Design',
            'ui': 'UI/UX Design',
            'ux': 'UI/UX Design',
            'backend': 'Backend Development',
            'api': 'Backend Development',
            'server': 'Backend Development',
            'automation': 'Other',
            'workflow': 'Other',
            'other': 'Other'
          };
          
          // Check if input matches any project type
          let matchedType = null;
          for (const [key, value] of Object.entries(projectTypeMap)) {
            if (trimmedProjectInput.includes(key)) {
              matchedType = value;
              break;
            }
          }
          
          if (matchedType) {
            projectType = matchedType;
          }
          // If no match, use the user input as-is (for "Other" category)
        }
        
        updatedForm.projectType = projectType;
        setRequirementsForm(updatedForm);
        nextStep = 4;
        botResponseText = 'Step 5 of 5: Describe your project requirements (features, goals, timeline, specific needs):';
        break;
      
      case 4: // Requirements
        // Validate requirements are not empty
        if (!userInput.trim()) {
          setIsTyping(false);
          const errorResponse = {
            id: messages.length + 2,
            text: 'Please describe your project requirements. This field is required:',
            sender: 'bot',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, errorResponse]);
          return;
        }
        
        // Validate minimum length
        if (userInput.trim().length < 10) {
          setIsTyping(false);
          const errorResponse = {
            id: messages.length + 2,
            text: 'Please provide more details about your project requirements (at least 10 characters):',
            sender: 'bot',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, errorResponse]);
          return;
        }
        
        updatedForm.requirements = userInput.trim();
        setRequirementsForm(updatedForm);
        
        // Submit the form
        setIsSubmittingRequirements(true);
        botResponseText = 'Thank you! I\'m submitting your project requirements. Please wait a moment...';
        
        const submitResponse = {
          id: messages.length + 2,
          text: botResponseText,
          sender: 'bot',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, submitResponse]);
        setIsTyping(false);
        
        // Submit requirements
        try {
          const result = await sendRequirementsFormEmail(updatedForm);
          
          if (result.success) {
            const successResponse = {
              id: messages.length + 3,
              text: `✅ Successfully submitted! Our team will review your details and get back to you within 24 hours.\n\nSubmitted:\n• Name: ${updatedForm.name}\n• Email: ${updatedForm.email}\n• Phone: ${updatedForm.phone}\n• Project: ${updatedForm.projectType}\n\nWe'll contact you soon!`,
              sender: 'bot',
              timestamp: new Date()
            };
            setMessages(prev => [...prev, successResponse]);
            
            // Reset form
            setIsCollectingRequirements(false);
            setRequirementsStep(0);
            setShowQuickActions(true); // Show quick actions after error
            setRequirementsForm({ name: '', email: '', phone: '', projectType: '', requirements: '' });
            setShowQuickActions(true); // Show quick actions after successful submission
          } else {
            const errorResponse = {
              id: messages.length + 3,
              text: 'I apologize, but there was an error submitting your requirements. Please try again or contact us directly:\n\nEmail: ' + contactInfo.email + '\nPhone: ' + contactInfo.phone,
              sender: 'bot',
              timestamp: new Date()
            };
            setMessages(prev => [...prev, errorResponse]);
            setIsCollectingRequirements(false);
            setRequirementsStep(0);
            setShowQuickActions(true); // Show quick actions after error
          }
        } catch (error) {
          const errorResponse = {
            id: messages.length + 3,
            text: 'I apologize, but there was an error submitting your requirements. Please try again or contact us directly:\n\nEmail: ' + contactInfo.email + '\nPhone: ' + contactInfo.phone,
            sender: 'bot',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, errorResponse]);
          setIsCollectingRequirements(false);
          setRequirementsStep(0);
        }
        
        setIsSubmittingRequirements(false);
        return;
    }

    setRequirementsStep(nextStep);
    
    setTimeout(() => {
      try {
        const botResponse = {
          id: messages.length + 2,
          text: botResponseText,
          sender: 'bot',
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, botResponse]);
        setIsTyping(false);
      } catch (error) {
        console.error('Error in requirements collection:', error);
        setIsTyping(false);
        const errorResponse = {
          id: messages.length + 2,
          text: 'I apologize, but I encountered an error. Please try again or type "cancel" to start over.',
          sender: 'bot',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorResponse]);
      }
    }, 500);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const quickActions = [
    { text: "Our Services", action: "What services do you offer?" },
    { text: "Contact Info", action: "How can I contact you?" },
    { text: "Submit Requirements", action: "I want to submit my project requirements" },
    { 
      text: "WhatsApp", 
      action: "contact",
      isLink: true,
      link: contactInfo.whatsapp
    },
    { 
      text: "Book Meeting", 
      action: "book",
      isLink: true,
      link: contactInfo.bookMeeting
    }
  ];

  const handleQuickAction = (actionItem) => {
    // Handle link actions (WhatsApp, Book Meeting)
    if (actionItem.isLink && actionItem.link) {
      window.open(actionItem.link, '_blank', 'noopener,noreferrer');
      return;
    }
    
    // Handle message actions - directly process the message
    const actionText = actionItem.action || actionItem.text;
    
    // Get the last bot message BEFORE adding the user message to the array
    let lastBotMsg = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].sender === 'bot') {
        lastBotMsg = messages[i].text;
        break;
      }
    }
    
    // Create user message
    const userMessage = {
      id: messages.length + 1,
      text: actionText,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);

    // Handle requirements collection if applicable
    if (isCollectingRequirements) {
      handleRequirementsCollection(actionText);
      return;
    }

      // Get bot response and send it
      setTimeout(() => {
        try {
          // Use the lastBotMsg we already retrieved before adding user message
          const response = getBotResponse(actionText, conversationContext, lastBotMsg);
          const botResponse = {
            id: messages.length + 2,
            text: typeof response === 'string' ? response : response.text,
            hasLinks: typeof response === 'object' ? response.hasLinks : false,
            sender: 'bot',
            timestamp: new Date()
          };
          
          setMessages(prev => [...prev, botResponse]);
          
          // Update conversation history
          updateConversationHistory(actionText, response);
          
          // Hide quick actions when user selects an action, unless response shows options
          const responseText = typeof response === 'string' ? response : response.text;
          const showsOptions = responseText.includes('1.') || 
                              responseText.includes('•') ||
                              responseText.includes('How would you like');
          setShowQuickActions(showsOptions);
          
          setIsTyping(false);
        } catch (error) {
          console.error('Error processing quick action:', error);
          setIsTyping(false);
          const errorResponse = {
            id: messages.length + 2,
            text: `I apologize, but I encountered an error. Please try again or contact us directly:\n\nEmail: ${contactInfo.email}\nPhone: ${contactInfo.phone}\nWhatsApp: Click to chat`,
            hasLinks: true,
            sender: 'bot',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, errorResponse]);
          setShowQuickActions(true);
        }
      }, 1000);
  };

  return (
    <>
      {/* Floating Chat Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 right-4 sm:bottom-5 sm:right-5 md:bottom-6 md:right-6 lg:bottom-8 lg:right-8 z-[1010] bg-primary text-white rounded-full p-2.5 sm:p-3 md:p-3.5 lg:p-4 shadow-lg hover:shadow-xl transition-shadow"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        aria-label="Open chat"
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Icon name="X" size={18} className="sm:w-5 sm:h-5 md:w-6 md:h-6" />
            </motion.div>
          ) : (
            <motion.div
              key="open"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Icon name="MessageCircle" size={18} className="sm:w-5 sm:h-5 md:w-6 md:h-6" />
            </motion.div>
          )}
        </AnimatePresence>
        {!isOpen && (
          <motion.span
            className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 bg-error rounded-full w-2.5 h-2.5 sm:w-3 sm:h-3"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ repeat: Infinity, duration: 2 }}
          />
        )}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-16 sm:bottom-20 md:bottom-24 right-2 sm:right-4 md:right-6 lg:right-8 z-[1010] w-[calc(100%-1rem)] sm:w-[calc(100%-2rem)] md:w-full max-w-sm sm:max-w-md lg:max-w-lg h-[calc(100vh-8rem)] sm:h-[500px] md:h-[550px] lg:h-[600px] max-h-[600px] bg-surface rounded-lg sm:rounded-xl shadow-2xl border border-border flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-primary text-white p-3 sm:p-4 rounded-t-lg sm:rounded-t-xl flex items-center justify-between flex-shrink-0">
              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="MessageCircle" size={16} className="sm:w-5 sm:h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-xs sm:text-sm truncate">Octalchip Support</h3>
                  <p className="text-[10px] sm:text-xs text-white/80 truncate">We're here to help</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white hover:text-white/80 transition-colors p-1 flex-shrink-0 ml-2 rounded-full btn-animate-pulse"
                aria-label="Close chat"
              >
                <Icon name="X" size={18} className="sm:w-5 sm:h-5" />
              </button>
            </div>

            {/* Messages Container */}
            <div className="flex-1 overflow-y-auto p-2 sm:p-3 md:p-4 space-y-2 sm:space-y-3 md:space-y-4 bg-background">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] sm:max-w-[80%] rounded-lg p-2 sm:p-2.5 md:p-3 ${
                      message.sender === 'user'
                        ? 'bg-primary text-white rounded-br-none'
                        : 'bg-surface text-text-primary border border-border rounded-bl-none'
                    }`}
                  >
                    {message.sender === 'bot' && message.hasLinks 
                      ? renderMessage(message.text, true)
                      : <p className="text-xs sm:text-sm whitespace-pre-line break-words">{message.text}</p>
                    }
                    <p className={`text-[10px] sm:text-xs mt-1 ${
                      message.sender === 'user' ? 'text-white/70' : 'text-text-secondary'
                    }`}>
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </motion.div>
              ))}
              
              {isTyping && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-surface border border-border rounded-lg rounded-bl-none p-3">
                    <div className="flex space-x-1">
                      <motion.div
                        className="w-2 h-2 bg-text-secondary rounded-full"
                        animate={{ y: [0, -8, 0] }}
                        transition={{ repeat: Infinity, duration: 0.6, delay: 0 }}
                      />
                      <motion.div
                        className="w-2 h-2 bg-text-secondary rounded-full"
                        animate={{ y: [0, -8, 0] }}
                        transition={{ repeat: Infinity, duration: 0.6, delay: 0.2 }}
                      />
                      <motion.div
                        className="w-2 h-2 bg-text-secondary rounded-full"
                        animate={{ y: [0, -8, 0] }}
                        transition={{ repeat: Infinity, duration: 0.6, delay: 0.4 }}
                      />
                    </div>
                  </div>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Actions */}
            {showQuickActions && !isCollectingRequirements && (
              <div className="px-2 sm:px-3 md:px-4 pt-2 pb-2 border-t border-border bg-background flex-shrink-0">
                <p className="text-[10px] sm:text-xs text-text-secondary mb-1.5 sm:mb-2">Quick actions:</p>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {quickActions.map((action, index) => {
                    const animations = ['btn-animate-bounce', 'btn-animate-pulse', 'btn-animate-scale', 'btn-animate-slide', 'btn-animate-glow', 'btn-animate-rotate', 'btn-animate-shake', 'btn-animate-float', 'btn-animate-wiggle'];
                    const animation = animations[index % animations.length];
                    return (
                      <button
                        key={index}
                        onClick={() => handleQuickAction(action)}
                        className={`text-[10px] sm:text-xs px-2 sm:px-2.5 md:px-3 py-1 sm:py-1.5 bg-surface border border-border rounded-full hover:bg-primary hover:text-white hover:border-primary transition-colors cursor-pointer whitespace-nowrap ${animation}`}
                      >
                        {action.text}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Requirements Collection Indicator */}
            {isCollectingRequirements && (
              <div className="px-2 sm:px-3 md:px-4 pt-2 pb-2 border-t border-border bg-primary/5 flex-shrink-0">
                <p className="text-[10px] sm:text-xs text-primary font-medium mb-1">
                  {requirementsStep === 0 && "Step 1 of 5: Your Name"}
                  {requirementsStep === 1 && "Step 2 of 5: Your Email"}
                  {requirementsStep === 2 && "Step 3 of 5: Your Phone"}
                  {requirementsStep === 3 && "Step 4 of 5: Project Type"}
                  {requirementsStep === 4 && "Step 5 of 5: Project Requirements"}
                </p>
                <button
                  onClick={() => {
                    setIsCollectingRequirements(false);
                    setRequirementsStep(0);
                    setRequirementsForm({ name: '', email: '', phone: '', projectType: '', requirements: '' });
                    setShowQuickActions(true); // Show quick actions after cancellation
                    const cancelResponseText = `No problem! I've cancelled the requirements submission. How would you like to proceed?\n\n1. Submit your project requirements\n2. Book a meeting\n3. Chat on WhatsApp\n4. Learn about our services\n\nWhat would you like to do? (Tell me the number or name)`;
                    const cancelMsg = {
                      id: messages.length + 1,
                      text: cancelResponseText,
                      sender: 'bot',
                      timestamp: new Date()
                    };
                    setMessages(prev => [...prev, cancelMsg]);
                    // Update conversation context to store the message with numbered options
                    setConversationContext(prev => ({
                      ...prev,
                      awaitingResponse: true,
                      lastOptions: null,
                      lastQuestion: null,
                      lastBotMessage: cancelResponseText
                    }));
                  }}
                  className="text-[10px] sm:text-xs text-text-secondary hover:text-primary underline rounded-full px-3 py-1 btn-animate-shake"
                >
                  Cancel
                </button>
              </div>
            )}

            {/* Input Area */}
            <form onSubmit={handleSendMessage} className="p-2 sm:p-3 md:p-4 border-t border-border bg-surface flex-shrink-0">
              <div className="flex space-x-1.5 sm:space-x-2">
                <input
                  ref={inputRef}
                  type={requirementsStep === 1 ? "email" : requirementsStep === 2 ? "tel" : "text"}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={
                    isCollectingRequirements
                      ? requirementsStep === 0
                        ? "Enter your name..."
                        : requirementsStep === 1
                        ? "Enter your email..."
                        : requirementsStep === 2
                        ? "Enter your phone number..."
                        : requirementsStep === 3
                        ? "Enter project type..."
                        : "Describe your project requirements..."
                      : "Type your message..."
                  }
                  disabled={isSubmittingRequirements}
                  className="flex-1 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <button
                  type="submit"
                  disabled={!inputValue.trim()}
                  className="bg-primary text-white px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-full hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center flex-shrink-0 btn-animate-bounce"
                  aria-label="Send message"
                >
                  <Icon name="ArrowRight" size={16} className="sm:w-5 sm:h-5" />
                </button>
              </div>
              <p className="text-[10px] sm:text-xs text-text-secondary mt-1.5 sm:mt-2 text-center">
                Press Enter to send, Shift+Enter for new line
              </p>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Chatbot;

